import { useRef, useState, useCallback, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Loader2, Copy, Trash2, Lock, Unlock, Eye, EyeOff, Plus, Type, Video, Music, Smile, Sparkles, CopyPlus, ClipboardPaste, Undo2, Redo2, Palette, ZoomIn, ZoomOut, Scissors, GitMerge, Layers as LayersIcon, ChevronDown, ChevronUp, ChevronRight, ChevronsDownUp, ChevronsUpDown, GripVertical, Move, AlignHorizontalJustifyCenter, AlignVerticalJustifyCenter, Crosshair, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Pencil, Magnet, Maximize2, Minimize2, LocateFixed, Link2, Unlink, Volume2, VolumeX, AudioLines, Mic, Circle, X, Image as ImageIcon, Eraser } from "lucide-react";
import { api } from "@/lib/api";
import { getWaveform, getVideoThumbnails } from "@/lib/mediaAnalysis";
import { IMAGE_EFFECTS } from "@/lib/imageEffects";
import { newElement, ELEMENT_TYPES } from "@/lib/elements";
import { Shapes, RefreshCw, Crop, MousePointerClick } from "lucide-react";
import { TtsPanel } from "./TtsPanel";
import { PresetsPanel } from "./PresetsPanel";
import TwoLineModal from "./TwoLineModal";
import { applyPresetToObject, resetPresetPatch, definitionFromObject, presetConfigPatch, phrasePatternPatch, layerPatternPatch } from "../lib/presets";
import { CaptionPlacement } from "@/components/CaptionPlacement";
import { CaptionFxControl } from "@/components/CaptionFxControl";
import { CaptionMotionControl } from "@/components/CaptionMotionControl";
import { askApplyScope } from "@/components/ApplyScopeDialog";
import { VideoCropOpacity } from "@/components/VideoCropOpacity";
import { baseClips, isEdited, resyncCaptionLayers } from "@/lib/syncEdit";
import CaptionPresetsLibrary from "./CaptionPresetsLibrary";
import { absUrl } from "@/lib/timeline";
import { YoutubeMediaDialog } from "@/components/YoutubeMediaDialog";
import { useFeatures, isFeatureVisible, isFeatureEnabled } from "@/lib/features";

const MODES = [{ v: "word", l: "Word by word" }, { v: "phrase", l: "Phrase by phrase" }, { v: "sentence", l: "Sentence by sentence" }];
const ROW_H = 46;
const GROUP_HEADER_H = 26;
const LABEL_W = 128;
const ZOOM_PRESETS = [0.1, 0.25, 0.5, 1, 2, 5, 10];
const TOL_PRESETS = [5, 10, 20];

const round = (n) => Math.round(n * 1000) / 1000;
const uid = () => crypto.randomUUID();
// Frame-accurate mm:ss.mmm readout (canonical source of truth = the timestamp).
const fmtMs = (t) => { t = Math.max(0, t || 0); const m = Math.floor(t / 60); const s = Math.floor(t % 60); const ms = Math.round((t - Math.floor(t)) * 1000); return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}.${String(ms).padStart(3, "0")}`; };

export default function CaptionTimeline({ projectId, isDemo, duration, currentTime, onSeek, layers, onChange, collapsed = false, onToggleCollapse, sel: selProp, setSel: setSelProp, fps = 30, isPlaying = false, mediaTracks = [], onMediaChange, videoUrl, expanded = false, onToggleExpanded, mediaAssets = [], onAssetAdded, onAssetsChange, onCaptionWordEdit, nleLayout = false, mediaPortalEl = null, inspectorPortalEl = null, projectMeta = null, inspectorVisible = true, baseAsset = null, presetPortalEl = null, onConfigPatch = null, captionConfig = null, actionsRef = null, onSelectionKind = null, onPause = null, onStyleWord = null, sentenceEndIds = null, transcriptSegments = null, onTranscribeAgain = null, onCropEdit = null, cropEditing = false, onTrackStart = null, onGenerateInstead = null }) {
  const scrollRef = useRef(null);
  useFeatures(); // subscribe to central feature-control state
  const [busy, setBusy] = useState(false);
  const [twoLineOpen, setTwoLineOpen] = useState(false);
  const [mode, setMode] = useState("word");
  const [selState, setSelState] = useState(null); // { layerId, ids: [objId] }
  const sel = selProp !== undefined ? selProp : selState;
  const setSel = setSelProp || setSelState;
  const [copiedStyle, setCopiedStyle] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [viewW, setViewW] = useState(800);
  const [dropLayerId, setDropLayerId] = useState(null);
  const [dragLayerId, setDragLayerId] = useState(null);
  const [overLayerId, setOverLayerId] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [renaming, setRenaming] = useState(null); // { id, value }
  // Batch 2 — timeline UX
  const [snapOn, setSnapOn] = useState(true);
  const [snapTolPx, setSnapTolPx] = useState(10);
  const [guide, setGuide] = useState(null); // { t } — transient snap guide
  const [dragInfo, setDragInfo] = useState(null); // { start, end, edge } — transient timing readout
  const [autoFollow, setAutoFollow] = useState(true);
  // Batch 3 — media tracks
  const [mediaSel, setMediaSel] = useState(null); // { trackId, objId }
  const [mediaTab, setMediaTab] = useState("all"); // Project Media tab: all|video|image|audio|elements
  const [gapSel, setGapSel] = useState(null); // { trackId, start, end }
  const [beatsOn, setBeatsOn] = useState(false);
  const [wave, setWave] = useState(null); // { peaks, duration, beats }
  const [thumbs, setThumbs] = useState(null); // { thumbs:[{t,url}], aspect }
  const [scrollLeft, setScrollLeft] = useState(0); // for virtualization
  // Track-group collapse state (CAPTIONS → IMAGES → VIDEO → AUDIO).
  const [groupCollapsed, setGroupCollapsed] = useState({});
  const toggleGroup = (g) => setGroupCollapsed((s) => ({ ...s, [g]: !s[g] }));
  const draggingRef = useRef(false);
  const lastAutoScrollRef = useRef(null);
  const prevPlayingRef = useRef(false);
  const histRef = useRef([]);   // combined undo history: { kind:'caption'|'media', prev }
  const redoHistRef = useRef([]);
  const dur = Math.max(duration || 1, 0.5, (mediaTracks || []).reduce((m, tr) => Math.max(m, ...(tr.objects || []).map((o) => o.end || 0), 0), 0));
  const FPS = fps > 0 ? fps : 30;
  const frameOf = (t) => Math.round(Math.max(0, t || 0) * FPS);
  const MIN_LEN = 0.05;

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setViewW(Math.max(320, Math.min(el.clientWidth || 800, window.innerWidth)));
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    measure();
    return () => ro.disconnect();
  }, []);

  const pxPerSec = (viewW * zoom) / dur;
  const innerW = Math.max(viewW, dur * pxPerSec);

  const currentTimeRef = useRef(currentTime);
  useEffect(() => { currentTimeRef.current = currentTime; });

  // ---- Snap (Phase 1/17/38): snap to playhead / boundaries / neighbouring object edges / beats ----
  const snapTargets = (excludeIds) => {
    const ts = [{ t: 0, k: "boundary" }, { t: dur, k: "boundary" }];
    const ph = currentTimeRef.current;
    if (ph != null) ts.push({ t: ph, k: "playhead" });
    const ls = layersRef.current || layers;
    ls.forEach((l) => (l.objects || []).forEach((o) => {
      if (excludeIds && excludeIds.includes(o.id)) return;
      ts.push({ t: o.start, k: "edge" }, { t: o.end, k: "edge" });
    }));
    (mediaTracks || []).forEach((tr) => (tr.objects || []).forEach((o) => {
      if (excludeIds && excludeIds.includes(o.id)) return;
      ts.push({ t: o.start, k: "edge" }, { t: o.end, k: "edge" });
    }));
    if (beatsOn && wave?.beats?.length) wave.beats.forEach((t) => ts.push({ t, k: "beat" }));
    return ts;
  };
  // Closest target within tolerance; ties broken by priority (playhead > edge > beat > boundary).
  const nearestSnap = (t, targets) => {
    if (!snapOn) return null;
    const rank = { playhead: 0, edge: 1, beat: 2, boundary: 3 };
    let best = null, bestPx = snapTolPx + 0.001;
    for (const c of targets) {
      const px = Math.abs(c.t - t) * pxPerSec;
      if (px < bestPx - 0.001 || (Math.abs(px - bestPx) <= 0.001 && best && rank[c.k] < rank[best.k])) { bestPx = px; best = c; }
    }
    return best ? best.t : null;
  };

  // ---- Zoom (Phase 7/8/9): change visual scale only; keep the playhead area in view ----
  const applyZoom = (nz) => {
    nz = Math.max(0.1, Math.min(10, Math.round(nz * 1000) / 1000));
    const el = scrollRef.current;
    let pivotT = currentTime || 0;
    let offset = el ? pivotT * pxPerSec - el.scrollLeft : 0;
    if (!el || offset < 0 || offset > el.clientWidth) {
      pivotT = el ? (el.scrollLeft + el.clientWidth / 2) / pxPerSec : 0;
      offset = el ? el.clientWidth / 2 : 0;
    }
    setZoom(nz);
    requestAnimationFrame(() => {
      const e2 = scrollRef.current; if (!e2) return;
      const nPx = (viewW * nz) / dur;
      const maxSL = Math.max(0, Math.max(viewW, dur * nPx) - e2.clientWidth);
      e2.scrollLeft = Math.max(0, Math.min(maxSL, pivotT * nPx - offset));
    });
  };
  const fitTimeline = () => applyZoom(1);
  // Fit EDITED content: zoom so the trimmed/edited duration fills the viewport (distinct from
  // Fit which resets to 100%). Uses the current timeline state, never the original upload length.
  const editedDuration = () => {
    let d = 0;
    (mediaTracks || []).forEach((tr) => (tr.objects || []).forEach((o) => { d = Math.max(d, o.end || 0); }));
    (layers || []).forEach((l) => (l.objects || []).forEach((o) => { d = Math.max(d, o.end || 0); }));
    return d > 0 ? d : (duration || 1);
  };
  const fitEditedTimeline = () => {
    const ed = editedDuration();
    if (ed <= 0) return;
    applyZoom(Math.max(0.05, Math.min(50, dur / ed)));
    const el = scrollRef.current; if (el) el.scrollLeft = 0;
    toast.success(`Fit to edited timeline (${ed.toFixed(1)}s)`);
  };

  // ---- Gaps + ripple delete (Phase: Timeline Enhancements) ----
  const trackGaps = (track) => {
    if (!track) return [];
    const clips = [...(track.objects || [])].sort((a, b) => (a.start || 0) - (b.start || 0));
    const gaps = []; let cursor = 0;
    clips.forEach((c) => { const s = c.start || 0; if (s - cursor > 0.06) gaps.push({ start: round(cursor), end: round(s) }); cursor = Math.max(cursor, c.end || 0); });
    return gaps;
  };
  // Delete a selected gap: shift every later clip (all media tracks) + caption left by the gap
  // duration. Durations/speed/source unchanged; linked pairs move consistently (same delta).
  const rippleDeleteGap = (gap) => {
    if (!gap) return;
    const delta = round(gap.end - gap.start);
    if (delta <= 0) return;
    const shift = (objs) => (objs || []).map((o) => ((o.start || 0) >= gap.start - 1e-3
      ? { ...o, start: round((o.start || 0) - delta), end: round((o.end || 0) - delta) } : o));
    commitMedia(mediaTracks.map((tr) => ({ ...tr, objects: shift(tr.objects) })));
    if ((layers || []).some((l) => (l.objects || []).some((o) => (o.start || 0) >= gap.start - 1e-3))) {
      commit(layers.map((l) => ({ ...l, objects: shift(l.objects) })));
    }
    setGapSel(null);
    toast.success("Gap removed — following clips shifted left");
  };

  const isTypingTarget = (el) => {
    if (!el) return false;
    const tag = (el.tagName || "").toUpperCase();
    return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || el.isContentEditable;
  };
  // C = split at playhead; Delete/Backspace on a selected gap = ripple delete. Never while typing.
  useEffect(() => {
    const onKey = (e) => {
      if (isTypingTarget(e.target)) return;
      if ((e.key === "Delete" || e.key === "Backspace") && gapSel && isFeatureEnabled("ripple_delete")) {
        e.preventDefault(); rippleDeleteGap(gapSel); return;
      }
      if (e.key.toLowerCase() === "c" && !e.metaKey && !e.ctrlKey && !e.altKey && mediaSel && isFeatureEnabled("trim_keyboard_shortcut")) {
        e.preventDefault(); splitMediaAtPlayhead();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gapSel, mediaSel, mediaTracks, layers, currentTime]);

  // ---- Auto-follow (Phase 4/5/6): keep the playhead centered during playback ----
  useEffect(() => {
    if (!isPlaying || !autoFollow || draggingRef.current) return;
    const el = scrollRef.current; if (!el) return;
    const target = Math.max(0, Math.min(innerW - el.clientWidth, (currentTime || 0) * pxPerSec - el.clientWidth / 2));
    lastAutoScrollRef.current = target;
    el.scrollLeft = target;
  }, [currentTime, isPlaying, autoFollow, pxPerSec, innerW]);
  // Pressing Play after manual navigation intelligently resumes auto-follow.
  useEffect(() => {
    if (isPlaying && !prevPlayingRef.current) setAutoFollow(true);
    prevPlayingRef.current = isPlaying;
  }, [isPlaying]);
  const onTimelineScroll = () => {
    const el = scrollRef.current; if (!el) return;
    setScrollLeft(el.scrollLeft);
    const la = lastAutoScrollRef.current;
    if (la != null && Math.abs(el.scrollLeft - la) < 3) return; // our own auto-scroll
    if (isPlaying && autoFollow) setAutoFollow(false); // user scrubbed → stop fighting them
  };
  const resumeAutoFollow = () => {
    setAutoFollow(true);
    const el = scrollRef.current;
    if (el) { const target = Math.max(0, Math.min(innerW - el.clientWidth, (currentTime || 0) * pxPerSec - el.clientWidth / 2)); lastAutoScrollRef.current = target; el.scrollLeft = target; }
  };
  // Phones: two-finger pinch on the timeline = zoom (keeps the pinch point steady via applyZoom).
  const zoomFnRef = useRef(null);
  zoomFnRef.current = { applyZoom, zoom };
  useEffect(() => {
    const el = scrollRef.current; if (!el) return undefined;
    let d0 = 0, z0 = 1, raf = 0;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
    const start = (e) => { if (e.touches.length === 2) { d0 = dist(e.touches); z0 = zoomFnRef.current.zoom; } };
    const move = (e) => {
      if (e.touches.length !== 2 || !d0) return;
      e.preventDefault();
      const nz = z0 * (dist(e.touches) / d0);
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => zoomFnRef.current.applyZoom(nz));
    };
    const end = (e) => { if (e.touches.length < 2) d0 = 0; };
    el.addEventListener("touchstart", start, { passive: true });
    el.addEventListener("touchmove", move, { passive: false });
    el.addEventListener("touchend", end);
    el.addEventListener("touchcancel", end);
    return () => { el.removeEventListener("touchstart", start); el.removeEventListener("touchmove", move); el.removeEventListener("touchend", end); el.removeEventListener("touchcancel", end); cancelAnimationFrame(raf); };
  }, []);
  // Ctrl/Cmd + wheel = zoom; plain wheel = horizontal scroll (non-passive so preventDefault works).
  useEffect(() => {
    const el = scrollRef.current; if (!el) return;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); applyZoom(e.deltaY < 0 ? zoom * 1.15 : zoom / 1.15); }
      else if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) { e.preventDefault(); el.scrollLeft += e.deltaY; if (isPlaying && autoFollow) setAutoFollow(false); }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoom, viewW, dur, isPlaying, autoFollow]);

  const commit = (next) => { histRef.current = [...histRef.current.slice(-80), { kind: "caption", prev: layers }]; redoHistRef.current = []; onChange(next); };
  const commitMedia = (next) => { histRef.current = [...histRef.current.slice(-80), { kind: "media", prev: mediaTracks }]; redoHistRef.current = []; onMediaChange && onMediaChange(next); };
  const snapshotCaption = () => { histRef.current = [...histRef.current.slice(-80), { kind: "caption", prev: layers }]; redoHistRef.current = []; };
  const snapshotMedia = () => { histRef.current = [...histRef.current.slice(-80), { kind: "media", prev: mediaTracks }]; redoHistRef.current = []; };
  const undo = () => {
    const h = histRef.current; if (!h.length) return;
    const e = h[h.length - 1];
    if (e.kind === "media") { redoHistRef.current = [...redoHistRef.current, { kind: "media", prev: mediaTracks }]; onMediaChange && onMediaChange(e.prev); }
    else { redoHistRef.current = [...redoHistRef.current, { kind: "caption", prev: layers }]; onChange(e.prev); }
    histRef.current = h.slice(0, -1);
  };
  const redo = () => {
    const r = redoHistRef.current; if (!r.length) return;
    const e = r[r.length - 1];
    if (e.kind === "media") { histRef.current = [...histRef.current, { kind: "media", prev: mediaTracks }]; onMediaChange && onMediaChange(e.prev); }
    else { histRef.current = [...histRef.current, { kind: "caption", prev: layers }]; onChange(e.prev); }
    redoHistRef.current = r.slice(0, -1);
  };

  const captionLayers = layers.filter((l) => l.type === "captions");
  const textLayers = layers.filter((l) => l.type === "text");
  const orderedLayers = layers.filter((l) => l.type === "captions" || l.type === "text");

  const moveLayer = (id, dir) => {
    const i = layers.findIndex((l) => l.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= layers.length) return;
    const next = [...layers];
    [next[i], next[j]] = [next[j], next[i]];
    commit(next);
  };

  // Drag-and-drop layer reorder. Moves the whole layer to a new z-order slot.
  // Order only — transcript, timings, styles and animations are untouched.
  const reorderLayers = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const from = layers.findIndex((l) => l.id === fromId);
    const to = layers.findIndex((l) => l.id === toId);
    if (from < 0 || to < 0) return;
    const next = [...layers];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
    toast.success("Layer reordered");
  };

  const createCaptions = async () => {
    setBusy(true);
    try {
      const { caption_layers } = await api.createCaptions(projectId, mode);
      const edited = isEdited(baseClips(mediaTracks), duration);
      commit(edited ? resyncCaptionLayers(caption_layers, transcriptSegments, mediaTracks).layers : caption_layers);
      toast.success(`Captions created — ${mode}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create captions."); }
    setBusy(false);
  };

  const [syncMenu, setSyncMenu] = useState(false);
  const clipRef = useRef(null);
  const [pickMode, setPickMode] = useState(false);
  const [picked, setPicked] = useState(() => new Set()); // "layerId:objId" picked in Select mode
  // Drop picks whose caption was deleted / split / merged.
  useEffect(() => {
    if (!picked.size) return;
    const live = new Set(layers.flatMap((l) => (l.objects || []).map((o) => `${l.id}:${o.id}`)));
    if ([...picked].some((k) => !live.has(k))) setPicked((p) => new Set([...p].filter((k) => live.has(k))));
  }, [layers]); // eslint-disable-line react-hooks/exhaustive-deps
  const capLayersAll = () => layers.filter((l) => l.type === "captions" || l.type === "text");
  const captionAt = (t) => { for (const l of capLayersAll()) { const o = (l.objects || []).find((x) => t >= x.start && t < x.end); if (o) return { l, o }; } return null; };
  // Resolve a scope answer to target keys.
  const scopeTargets = (mode) => {
    const out = new Set();
    if (mode === "all") capLayersAll().forEach((l) => (l.objects || []).forEach((o) => out.add(`${l.id}:${o.id}`)));
    else if (mode === "selected") { if (picked.size) picked.forEach((k) => out.add(k)); else if (sel && sel.ids.length > 1) sel.ids.forEach((id) => out.add(`${sel.layerId}:${id}`)); }
    else if (mode === "current") {
      if (sel && sel.ids[0]) out.add(`${sel.layerId}:${sel.ids[0]}`);
      else { const hit = captionAt(currentTime || 0); if (hit) out.add(`${hit.l.id}:${hit.o.id}`); }
    }
    return out;
  };
  const scopeInfo = () => {
    const cur = sel && sel.ids[0] ? (layers.find((l) => l.id === sel.layerId)?.objects || []).find((o) => o.id === sel.ids[0]) : captionAt(currentTime || 0)?.o;
    return { current: cur ? cur.text || "caption" : null, selected: picked.size || (sel && sel.ids.length > 1 ? sel.ids.length : 0), total: capLayersAll().reduce((n, l) => n + (l.objects || []).length, 0) };
  };
  const askScope = async (what) => askApplyScope({ what, ...scopeInfo() });
  const patchTargets = (targets, patch) => commit(layers.map((l) => ({ ...l, objects: (l.objects || []).map((o) => (targets.has(`${l.id}:${o.id}`) ? { ...o, ...patch } : o)) })));
  // Effect / motion type changes ask where to apply; fine-tuning (sliders) stays on the current selection.
  const askAndPatch = async (patch, what) => {
    const mode = await askScope(what);
    if (!mode) return;
    const tg = scopeTargets(mode);
    if (!tg.size) { toast.error("No captions to apply to."); return; }
    patchTargets(tg, patch);
    toast.success(`Applied ${what} to ${tg.size} caption${tg.size > 1 ? "s" : ""}`);
  };
  const requestPreset = async (preset) => {
    const mode = await askScope(preset.name);
    if (mode) applyPresetTo(preset, mode, true);
  };
  const resyncToEdit = () => {
    setSyncMenu(false);
    const { layers: next, moved, dropped } = resyncCaptionLayers(layers, transcriptSegments, mediaTracks);
    if (!moved && !dropped) { toast.message("Nothing to re-sync — captions aren't linked to the transcript or the video has no base clip."); return; }
    commit(next);
    toast.success(`Captions re-synced to the edited video${dropped ? ` · ${dropped} cut-out caption${dropped > 1 ? "s" : ""} removed` : ""}`);
  };
  const addLayer = () => {
    const n = layers.length + 1;
    commit([...layers, { id: uid(), name: `Captions ${n}`, type: "text", locked: false, hidden: false, objects: [] }]);
    toast.success("Caption layer added");
  };

  const addTwoLine = (objects) => {
    const n = layers.filter((l) => l.type === "text").length + 1;
    commit([...layers, { id: uid(), name: `Two-Line ${n}`, type: "text", locked: false, hidden: false, objects: objects.map((o) => ({ ...o, id: uid() })) }]);
    toast.success("Two-line title added — edit each line in the inspector");
  };
  const patchLayer = (id, patch) => commit(layers.map((l) => l.id === id ? { ...l, ...patch } : l));
  const startRename = (layer) => setRenaming({ id: layer.id, value: layer.name });
  const commitRename = () => {
    if (!renaming) return;
    const v = (renaming.value || "").trim();
    if (v) patchLayer(renaming.id, { name: v });
    setRenaming(null);
  };
  const requestDeleteLayer = (layer) => {
    const count = (layer.objects || []).length;
    if (count > 0) setConfirmDel({ id: layer.id, name: layer.name, count });
    else { commit(layers.filter((l) => l.id !== layer.id)); setSel(null); }
  };
  const confirmDeleteLayer = () => { commit(layers.filter((l) => l.id !== confirmDel.id)); setSel(null); setConfirmDel(null); };
  const duplicateLayer = (layer) => {
    commit([...layers, { ...layer, id: uid(), name: `${layer.name} copy`, type: "text", objects: (layer.objects || []).map((o) => ({ ...o, id: uid() })) }]);
    toast.success("Layer duplicated");
  };

  const updateObj = (layerId, objId, patch) => {
    onChange(layers.map((l) => l.id !== layerId ? l : { ...l, objects: l.objects.map((o) => o.id === objId ? { ...o, ...patch } : o) }));
  };
  // Editing caption text must keep o.words in lock-step, because BOTH the preview
  // (lib/animator drawCaptionFrame) and the server export (animation_engine) render
  // from o.words — not o.text. Without this, edits silently don't appear. Preserves
  // per-word ids/timing when the token count is unchanged; else redistributes evenly.
  const setCaptionText = (layerId, objId, text) => {
    const layer = layers.find((l) => l.id === layerId);
    const o = layer && (layer.objects || []).find((x) => x.id === objId);
    if (!o) return;
    const toks = String(text).split(/\s+/).filter(Boolean);
    const old = (o.words && o.words.length) ? o.words : [];
    const s = o.start || 0, e = (o.end != null ? o.end : s + 0.5), dur = Math.max(0.05, e - s);
    let words;
    if (toks.length === 0) words = [];
    else if (toks.length === old.length) words = old.map((w, i) => ({ ...w, word: toks[i] }));
    else { const step = dur / toks.length; words = toks.map((tok, i) => ({ word: tok, word_id: (old[i] && old[i].word_id) || null, start: round(s + i * step), end: round(s + (i + 1) * step) })); }
    updateObj(layerId, objId, { text, words, textLocked: true });
  };
  // Phase 2 — when a caption's spelling is fixed in the NLE, offer to update the matching
  // transcript word too (never silently). Only for a clean spelling fix (same word count,
  // caption links to real transcript word_ids). Editor shows the Yes/No prompt.
  const offerTranscriptSync = (obj) => {
    if (!obj || !onCaptionWordEdit) return;
    const words = (obj.words || []).filter((w) => w.word_id);
    if (!words.length) return;
    const newTok = (obj.text || "").trim().split(/\s+/).filter(Boolean);
    if (newTok.length !== (obj.words || []).length) return; // structure changed, not a spelling fix
    const changes = [];
    (obj.words || []).forEach((w, i) => {
      const to = newTok[i];
      if (w.word_id && to && to !== w.word) changes.push({ word_id: w.word_id, from: w.word, to });
    });
    if (changes.length) onCaptionWordEdit(changes);
  };
  const deleteObjs = (layerId, ids) => { commit(layers.map((l) => l.id !== layerId ? l : { ...l, objects: l.objects.filter((o) => !ids.includes(o.id)) })); setSel(null); };

  const selObjs = () => {
    if (!sel) return [];
    const layer = layers.find((l) => l.id === sel.layerId);
    return layer ? layer.objects.filter((o) => sel.ids.includes(o.id)) : [];
  };

  const lastBlockClick = useRef({ id: null, t: 0 });
  // Touch: tap selects, long-press (≈0.45s) adds/removes a caption from the multi-selection,
  // and only an already-selected caption can be dragged (so finger swipes still scroll the timeline).
  const pressRef = useRef({ timer: 0, suppress: false });
  const blockPointerDown = (e, layer, obj, kind) => {
    if (e.pointerType !== "touch") return startDrag(e, layer, obj, kind);
    if (sel?.layerId === layer.id && sel.ids?.includes(obj.id) && kind === "move" && sel.ids.length === 1) return startDrag(e, layer, obj, kind);
    e.stopPropagation();
    const x0 = e.clientX, y0 = e.clientY;
    const cancel = () => { clearTimeout(pressRef.current.timer); window.removeEventListener("pointermove", mv); window.removeEventListener("pointerup", cancel); };
    const mv = (ev) => { if (Math.hypot(ev.clientX - x0, ev.clientY - y0) > 10) cancel(); };
    window.addEventListener("pointermove", mv);
    window.addEventListener("pointerup", cancel);
    pressRef.current.timer = setTimeout(() => {
      cancel();
      navigator.vibrate?.(30);
      pressRef.current.suppress = true;
      setSel((s) => {
        if (!s || s.layerId !== layer.id) return { layerId: layer.id, ids: [obj.id] };
        const ids = s.ids.includes(obj.id) ? s.ids.filter((x) => x !== obj.id) : [...s.ids, obj.id];
        return ids.length ? { layerId: layer.id, ids } : null;
      });
    }, 450);
  };
  const clickBlock = (e, layer, obj) => {
    e.stopPropagation();
    if (pressRef.current.suppress) { pressRef.current.suppress = false; return; }
    if (pickMode) {
      const k = `${layer.id}:${obj.id}`;
      setPicked((p) => { const n = new Set(p); n.has(k) ? n.delete(k) : n.add(k); return n; });
      setSel({ layerId: layer.id, ids: [obj.id] });
      return;
    }
    const now = Date.now(), lc = lastBlockClick.current;
    lastBlockClick.current = { id: obj.id, t: now };
    // Block remounts on every render, so native dblclick never fires — detect it here.
    if (lc.id === obj.id && now - lc.t < 400 && onStyleWord) {
      const w0 = (obj.words || [])[0]; const wid = w0?.word_id || obj.word_id;
      if (wid) { onStyleWord(wid, w0?.word || obj.text); return; }
    }
    const additive = e.metaKey || e.ctrlKey || e.shiftKey;
    if (additive && sel && sel.layerId === layer.id) {
      const ids = sel.ids.includes(obj.id) ? sel.ids.filter((x) => x !== obj.id) : [...sel.ids, obj.id];
      setSel(ids.length ? { layerId: layer.id, ids } : null);
    } else {
      setSel({ layerId: layer.id, ids: [obj.id] });
      seekFromClientX(e.clientX);
    }
  };

  const layerYs = () => {
    // vertical center of each ordered caption/text layer within the track area.
    // Captions are the FIRST group (after its group header), so rows start at base + header.
    const rect = scrollRef.current?.getBoundingClientRect();
    const base = rect ? rect.top - (scrollRef.current.scrollTop || 0) : 0;
    const startY = base + GROUP_HEADER_H;
    return orderedLayers.map((l, i) => ({ id: l.id, locked: l.locked, top: startY + i * ROW_H, bottom: startY + (i + 1) * ROW_H }));
  };

  const startDrag = useCallback((e, layer, obj, kind) => {
    e.stopPropagation();
    if (layer.locked) return;
    histRef.current = [...histRef.current.slice(-80), { kind: "caption", prev: layers }]; redoHistRef.current = [];
    draggingRef.current = true;
    const startX = e.clientX;
    const o0 = { ...obj };
    const bounds = kind === "move" ? layerYs() : null;
    const targets = snapTargets([obj.id]);
    const move = (ev) => {
      const dt = (ev.clientX - startX) / pxPerSec;
      if (kind === "move") {
        const len = o0.end - o0.start;
        let ns = o0.start + dt;
        // Snap whichever edge (start/end) is closest to a target.
        const snapS = nearestSnap(ns, targets);
        const snapE = nearestSnap(ns + len, targets);
        let g = null;
        if (snapS != null && (snapE == null || Math.abs(snapS - ns) <= Math.abs(snapE - (ns + len)))) { ns = snapS; g = snapS; }
        else if (snapE != null) { ns = snapE - len; g = snapE; }
        ns = Math.max(0, Math.min(dur - len, ns));
        updateObj(layer.id, obj.id, { start: round(ns), end: round(ns + len), timeLocked: true });
        setGuide(g != null ? { t: g } : null);
        setDragInfo({ start: round(ns), end: round(ns + len), edge: "both" });
        const hit = bounds.find((b) => ev.clientY >= b.top && ev.clientY < b.bottom);
        setDropLayerId(hit && hit.id !== layer.id && !hit.locked ? hit.id : null);
      } else if (kind === "left") {
        let ns = o0.start + dt;
        const s = nearestSnap(ns, targets);
        if (s != null) ns = s;
        ns = Math.max(0, Math.min(o0.end - MIN_LEN, ns));
        updateObj(layer.id, obj.id, { start: round(ns), timeLocked: true });
        setGuide(s != null ? { t: s } : null);
        setDragInfo({ start: round(ns), end: o0.end, edge: "left" });
      } else {
        let ne = o0.end + dt;
        const s = nearestSnap(ne, targets);
        if (s != null) ne = s;
        ne = Math.min(dur, Math.max(o0.start + MIN_LEN, ne));
        updateObj(layer.id, obj.id, { end: round(ne), timeLocked: true });
        setGuide(s != null ? { t: s } : null);
        setDragInfo({ start: o0.start, end: round(ne), edge: "right" });
      }
    };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
      draggingRef.current = false;
      setGuide(null); setDragInfo(null);
      setDropLayerId((target) => {
        if (kind === "move" && target && target !== layer.id) {
          onChange((() => {
            const cur = layersRef.current;
            const src = cur.find((l) => l.id === layer.id);
            const moved = src?.objects.find((o) => o.id === obj.id);
            if (!moved) return cur;
            return cur.map((l) => {
              if (l.id === layer.id) return { ...l, objects: l.objects.filter((o) => o.id !== obj.id) };
              if (l.id === target) return { ...l, objects: [...l.objects, { ...moved }] };
              return l;
            });
          })());
          setSel({ layerId: target, ids: [obj.id] });
          toast.success("Phrase moved to another layer");
        }
        return null;
      });
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  }, [dur, layers, pxPerSec, snapOn, snapTolPx]);

  const layersRef = useRef(layers);
  useEffect(() => { layersRef.current = layers; }, [layers]);

  const mediaRef = useRef(mediaTracks);
  useEffect(() => { mediaRef.current = mediaTracks; }, [mediaTracks]);

  // ---- Batch 3: load waveform (audio) + thumbnails (video) for the source media ----
  useEffect(() => {
    if (!videoUrl) return;
    let alive = true;
    getWaveform(videoUrl).then((w) => { if (alive) setWave(w); }).catch(() => {});
    getVideoThumbnails(videoUrl, 14).then((t) => { if (alive) setThumbs(t); }).catch(() => {});
    return () => { alive = false; };
  }, [videoUrl]);

  const mediaClip = () => {
    if (!mediaSel) return null;
    const tr = mediaTracks.find((t) => t.id === mediaSel.trackId);
    return tr ? (tr.objects || []).find((o) => o.id === mediaSel.objId) : null;
  };
  const trPreviewRef = useRef(null);
  const patchMediaObj = (trackId, objId, patch) => {
    onMediaChange && onMediaChange(mediaTracks.map((tr) => tr.id !== trackId ? tr : { ...tr, objects: tr.objects.map((o) => o.id === objId ? { ...o, ...patch } : o) }));
  };
  const patchMediaTrack = (trackId, patch) => commitMedia(mediaTracks.map((tr) => tr.id === trackId ? { ...tr, ...patch } : tr));

  // Are the video + audio clips currently linked?
  const linkedPair = () => {
    const v = (mediaTracks.find((t) => t.type === "video")?.objects || [])[0];
    const a = (mediaTracks.find((t) => t.type === "audio")?.objects || [])[0];
    return { v, a, linked: !!(v && a && v.linked_id === a.id && a.linked_id === v.id) };
  };
  const toggleLink = () => {
    const { v, a, linked } = linkedPair();
    if (!v || !a) return;
    commitMedia(mediaTracks.map((tr) => ({
      ...tr,
      objects: tr.objects.map((o) => {
        if (o.id === v.id) return { ...o, linked_id: linked ? null : a.id };
        if (o.id === a.id) return { ...o, linked_id: linked ? null : v.id };
        return o;
      }),
    })));
    toast.success(linked ? "Audio unlinked from video" : "Audio relinked to video");
  };

  // Drag/trim a media clip (with snapping + linked mirroring for move/trim).
  const startMediaDrag = useCallback((e, track, obj, kind) => {
    e.stopPropagation();
    if (track.locked) return;
    histRef.current = [...histRef.current.slice(-80), { kind: "media", prev: mediaRef.current }]; redoHistRef.current = [];
    draggingRef.current = true;
    const startX = e.clientX;
    const o0 = { ...obj };
    const targets = snapTargets([obj.id]);
    const linkId = o0.linked_id;
    const apply = (updater) => {
      const cur = mediaRef.current;
      onMediaChange && onMediaChange(cur.map((tr) => ({ ...tr, objects: tr.objects.map((o) => {
        if (o.id === obj.id) return updater(o, o0);
        if (linkId && o.id === linkId) return updater(o, o0); // mirror the linked clip
        return o;
      }) })));
    };
    const move = (ev) => {
      const dt = (ev.clientX - startX) / pxPerSec;
      if (kind === "move") {
        const len = o0.end - o0.start;
        let ns = o0.start + dt;
        const sS = nearestSnap(ns, targets); const sE = nearestSnap(ns + len, targets);
        let g = null;
        if (sS != null && (sE == null || Math.abs(sS - ns) <= Math.abs(sE - (ns + len)))) { ns = sS; g = sS; }
        else if (sE != null) { ns = sE - len; g = sE; }
        ns = Math.max(0, Math.min(dur - len, ns));
        apply((o) => ({ ...o, start: round(ns), end: round(ns + len) }));
        setGuide(g != null ? { t: g } : null); setDragInfo({ start: round(ns), end: round(ns + len), edge: "both" });
      } else if (kind === "left") {
        let ns = o0.start + dt; const s = nearestSnap(ns, targets); if (s != null) ns = s;
        ns = Math.max(0, Math.min(o0.end - MIN_LEN, ns));
        const dstart = ns - o0.start;
        apply((o, base) => ({ ...o, start: round(ns), source_start: round((base.source_start ?? 0) + dstart) }));
        setGuide(s != null ? { t: s } : null); setDragInfo({ start: round(ns), end: o0.end, edge: "left" });
      } else {
        let ne = o0.end + dt; const s = nearestSnap(ne, targets); if (s != null) ne = s;
        ne = Math.min(dur, Math.max(o0.start + MIN_LEN, ne));
        const dend = ne - o0.end;
        apply((o, base) => ({ ...o, end: round(ne), source_end: round((base.source_end ?? base.end) + dend) }));
        setGuide(s != null ? { t: s } : null); setDragInfo({ start: o0.start, end: round(ne), edge: "right" });
      }
    };
    const up = () => { window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); draggingRef.current = false; setGuide(null); setDragInfo(null); };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  }, [dur, pxPerSec, snapOn, snapTolPx, beatsOn, wave]);

  const splitMediaAtPlayhead = () => {
    const clip = mediaClip(); if (!clip) return;
    const t = currentTime || 0;
    if (t <= clip.start + MIN_LEN || t >= clip.end - MIN_LEN) { toast.error("Move the playhead inside the clip to split."); return; }
    const frac = (t - clip.start) / (clip.end - clip.start);
    const srcMid = round((clip.source_start ?? 0) + frac * ((clip.source_end ?? clip.end) - (clip.source_start ?? 0)));
    const track = mediaTracks.find((tr) => tr.id === mediaSel.trackId);
    const linkId = clip.linked_id;
    const mk = () => {
      const aId = uid(), bId = uid();
      const a = { ...clip, id: aId, end: round(t), source_end: srcMid, linked_id: null };
      const b = { ...clip, id: bId, start: round(t), source_start: srcMid, linked_id: null };
      return [a, b, aId, bId];
    };
    const [va, vb, vaId, vbId] = mk();
    let next = mediaTracks.map((tr) => tr.id !== track.id ? tr : { ...tr, objects: tr.objects.flatMap((o) => o.id === clip.id ? [va, vb] : [o]) });
    // If linked, split the partner clip at the same time and re-link the halves.
    if (linkId) {
      next = next.map((tr) => {
        const partner = (tr.objects || []).find((o) => o.id === linkId);
        if (!partner) return tr;
        const pfrac = (t - partner.start) / (partner.end - partner.start);
        const pMid = round((partner.source_start ?? 0) + pfrac * ((partner.source_end ?? partner.end) - (partner.source_start ?? 0)));
        const pa = { ...partner, id: uid(), end: round(t), source_end: pMid, linked_id: vaId };
        const pb = { ...partner, id: uid(), start: round(t), source_start: pMid, linked_id: vbId };
        va.linked_id = pa.id; vb.linked_id = pb.id;
        return { ...tr, objects: tr.objects.flatMap((o) => o.id === partner.id ? [pa, pb] : [o]) };
      });
    }
    commitMedia(next);
    setMediaSel({ trackId: track.id, objId: vaId });
    toast.success("Clip split at playhead");
  };
  const duplicateMediaClip = () => {
    const clip = mediaClip(); if (!clip) return;
    const track = mediaTracks.find((tr) => tr.id === mediaSel.trackId);
    const len = clip.end - clip.start;
    let ns = clip.end, ne = ns + len; if (ne > dur) { ne = dur; ns = Math.max(0, ne - len); }
    const nid = uid();
    const copy = { ...clip, id: nid, start: round(ns), end: round(ne), linked_id: null };
    commitMedia(mediaTracks.map((tr) => tr.id !== track.id ? tr : { ...tr, objects: [...tr.objects, copy] }));
    setMediaSel({ trackId: track.id, objId: nid });
    toast.success("Clip duplicated");
  };
  const deleteMediaClip = () => {
    const clip = mediaClip(); if (!clip) return;
    commitMedia(mediaTracks.map((tr) => tr.id !== mediaSel.trackId ? tr : { ...tr, objects: tr.objects.filter((o) => o.id !== clip.id) }));
    setMediaSel(null);
    toast.success("Clip deleted");
  };

  // ---- Uploads → Project Media asset ONLY (never auto-added to the timeline) ----
  const audioInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const voiceInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const uploadMediaInputRef = useRef(null); // single "Upload Media" picker (auto-detects type)
  const [rembgBusy, setRembgBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [ytKind, setYtKind] = useState(null); // "video" | "audio" → YouTube link dialog
  const [recording, setRecording] = useState(false);
  const [showMedia, setShowMedia] = useState(false);
  const recRef = useRef(null);

  // Detect the media kind from a File (MIME first, extension fallback).
  const detectKind = (file) => {
    const mt = (file.type || "").toLowerCase();
    if (mt.startsWith("video/")) return "video";
    if (mt.startsWith("image/")) return "image";
    if (mt.startsWith("audio/")) return "audio";
    const ext = (file.name || "").toLowerCase().split(".").pop();
    if (["mp4", "mov", "webm", "m4v", "avi", "mkv"].includes(ext)) return "video";
    if (["png", "jpg", "jpeg", "webp", "gif", "bmp", "heic"].includes(ext)) return "image";
    if (["mp3", "wav", "m4a", "aac", "ogg", "flac"].includes(ext)) return "audio";
    return null;
  };

  // Upload one or more files into Project Media. Auto-detects type. Does NOT touch the timeline.
  const uploadToLibrary = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    let added = 0, skipped = 0;
    for (const file of list) {
      const kind = detectKind(file);
      if (!kind) { skipped++; continue; }
      try {
        const { asset } = await api.uploadMedia(projectId, file, kind);
        onAssetAdded && onAssetAdded(asset);
        added++;
      } catch (e) { toast.error(e?.response?.data?.detail || `Could not upload ${file.name}.`); }
    }
    setUploading(false);
    if (added) toast.success(`Added ${added} file${added > 1 ? "s" : ""} to Project Media — drag onto the timeline to use`);
    if (skipped) toast.error(`${skipped} file${skipped > 1 ? "s" : ""} skipped — unsupported media type`);
  };

  // Paste an image/media from the clipboard into Project Media (never onto the timeline).
  const pasteFromClipboard = async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.read) { toast.error("Clipboard paste isn't supported in this browser."); return; }
      const items = await navigator.clipboard.read();
      const files = [];
      for (const it of items) {
        const type = it.types.find((t) => t.startsWith("image/") || t.startsWith("video/") || t.startsWith("audio/"));
        if (!type) continue;
        const blob = await it.getType(type);
        const ext = type.split("/")[1] || "png";
        const stamp = new Date().toISOString().slice(0, 10);
        files.push(new File([blob], `clipboard-${stamp}-${String(Date.now()).slice(-4)}.${ext}`, { type }));
      }
      if (!files.length) { toast.error("Clipboard content isn't a supported media file."); return; }
      await uploadToLibrary(files);
    } catch (e) {
      toast.error("Couldn't read the clipboard. Copy an image, then try again.");
    }
  };

  const addAudioTrackFromFile = async (file) => { if (file) await uploadToLibrary([file]); };
  const addMusic = (file) => uploadToLibrary(file ? [file] : []);
  const addVoice = (file) => uploadToLibrary(file ? [file] : []);
  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: "audio/webm" });
        await uploadToLibrary([new File([blob], `voiceover-${Date.now()}.webm`, { type: "audio/webm" })]);
      };
      recRef.current = mr; mr.start(); setRecording(true);
      toast.message("Recording voiceover… click Stop when done");
    } catch { toast.error("Microphone permission denied."); }
  };
  const stopRec = () => { try { recRef.current?.stop(); } catch { /* noop */ } setRecording(false); };
  const addVideoClip = (file) => uploadToLibrary(file ? [file] : []);
  const addImageAsset = (file) => uploadToLibrary(file ? [file] : []);
  const removeAssetBg = async (asset) => {
    if (!mediaSel) return;
    setRembgBusy(true);
    const tid = toast.loading("Removing background… this can take a few seconds");
    try {
      let res = await api.removeBg(projectId, asset.id);
      const jobId = res.job_id;
      let tries = 0;
      while (res.status !== "ready" && res.status !== "failed" && tries < 120) {
        await new Promise((r) => setTimeout(r, 1500));
        res = await api.removeBgStatus(projectId, jobId);
        tries += 1;
      }
      if (res.status !== "ready" || !res.asset) throw new Error(res.error || "Background removal timed out.");
      const out = res.asset;
      onAssetAdded && onAssetAdded(out);
      // Keep BOTH the original and the cut-out on the clip so Original/Removed can toggle
      // without re-processing. Switch the clip to show the transparent cut-out now.
      patchMediaObj(mediaSel.trackId, mediaSel.objId, {
        original_asset_id: asset.id, original_asset_url: asset.url, original_name: asset.name,
        cutout_asset_id: out.id, cutout_asset_url: out.url, cutout_name: out.name,
        asset_id: out.id, asset_url: out.url, name: out.name, bgRemoved: true,
      });
      toast.success(res.cached ? "Background removed (reused a cached cut-out)" : "Background removed — transparent copy applied", { id: tid });
    } catch (e) { toast.error(e?.response?.data?.detail || e?.message || "Background removal failed.", { id: tid }); }
    setRembgBusy(false);
  };
  const toggleBgRemoved = (on) => {
    if (!mediaSel || !mediaClipSel) return;
    if (on) {
      patchMediaObj(mediaSel.trackId, mediaSel.objId, {
        asset_id: mediaClipSel.cutout_asset_id, asset_url: mediaClipSel.cutout_asset_url,
        name: mediaClipSel.cutout_name, bgRemoved: true,
      });
    } else {
      patchMediaObj(mediaSel.trackId, mediaSel.objId, {
        asset_id: mediaClipSel.original_asset_id, asset_url: mediaClipSel.original_asset_url,
        name: mediaClipSel.original_name, bgRemoved: false,
      });
    }
  };
  const addAssetToTimeline = (asset, atTime = null) => {
    const t0base = atTime != null ? round(Math.max(0, atTime)) : null;
    if (asset.kind === "image") {
      const t0 = t0base != null ? t0base : round(currentTime || 0), len = 5;
      const clip = { id: uid(), kind: "image", asset_id: asset.id, asset_url: asset.url, name: asset.name, start: t0, end: round(t0 + len), source_start: 0, source_end: len, x: 0.5, y: 0.5, scale: 0.5, opacity: 1 };
      const trackId = uid();
      const n = mediaTracks.filter((t) => t.type === "image").length + 1;
      commitMedia([...mediaTracks, { id: trackId, type: "image", name: `Image ${n}`, locked: false, hidden: false, muted: false, objects: [clip] }]);
      setMediaSel({ trackId, objId: clip.id });
      toast.success(`Added image "${asset.name}" to the timeline`);
      return;
    }
    if (asset.kind === "audio") {
      const t0 = t0base != null ? t0base : round(currentTime || 0), len = asset.duration || 5;
      const clip = { id: uid(), kind: "audio", asset_id: asset.id, asset_url: asset.url, asset_duration: asset.duration, name: asset.name, start: t0, end: round(t0 + len), source_start: 0, source_end: round(len), volume: 1, muted: false, fade_in: 0, fade_out: 0, linked_id: null };
      const trackId = uid();
      const n = mediaTracks.filter((t) => t.type === "audio" && (t.name || "").startsWith("Music")).length + 1;
      commitMedia([...mediaTracks, { id: trackId, type: "audio", name: `Music ${n}`, locked: false, hidden: false, muted: false, objects: [clip] }]);
      setMediaSel({ trackId, objId: clip.id });
    } else {
      const vt = mediaTracks.find((t) => t.type === "video"); if (!vt) return;
      const lastEnd = (vt.objects || []).reduce((m, o) => Math.max(m, o.end || 0), 0), len = asset.duration || 5;
      const start = t0base != null ? t0base : round(lastEnd);
      // The base video (is_base) is the canonical main source → reference it 1:1 with no
      // asset_id/asset_url so it plays the project's original video (and survives orphan repair).
      const clip = asset.is_base
        ? { id: uid(), kind: "video", name: asset.name, start, end: round(start + len), source_start: 0, source_end: round(len), volume: 1, muted: false, linked_id: null }
        : { id: uid(), kind: "video", asset_id: asset.id, asset_url: asset.url, asset_duration: asset.duration, name: asset.name, start, end: round(start + len), source_start: 0, source_end: round(len), volume: 1, muted: false, linked_id: null };
      commitMedia(mediaTracks.map((t) => t.id !== vt.id ? t : { ...t, objects: [...t.objects, clip] }));
      setMediaSel({ trackId: vt.id, objId: clip.id });
    }
    toast.success(`Added "${asset.name}" to the timeline`);
  };

  // ---- Design ELEMENTS (shapes/lines/arrows) ----
  const addElement = (type = "rect") => {
    const el = newElement(type, currentTime || 0);
    const trackId = uid();
    const n = mediaTracks.filter((t) => t.type === "element").length + 1;
    commitMedia([...mediaTracks, { id: trackId, type: "element", name: `Element ${n}`, locked: false, hidden: false, muted: false, objects: [el] }]);
    setMediaSel({ trackId, objId: el.id });
    onSelectionKind && onSelectionKind("element");
    toast.success(`Added ${type} element`);
  };
  // ---- Text to Speech (ElevenLabs) ----
  const [ttsOpen, setTtsOpen] = useState(false);
  const [ttsInit, setTtsInit] = useState({ text: "", atTime: null });
  const openTts = (init = {}) => { setTtsInit({ text: init.text || "", atTime: init.atTime ?? null }); setTtsOpen(true); };
  const onTtsGenerated = (asset, opts) => {
    onAssetAdded && onAssetAdded(asset);
    if (opts?.atTime != null) addAssetToTimeline(asset, opts.atTime);
  };
  // ---- Presets / Templates ----
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presetsVersion, setPresetsVersion] = useState(0);
  const captionObjCount = layers.filter((l) => l.type === "captions" || l.type === "text").reduce((n, l) => n + (l.objects || []).length, 0);
  const applyPresetTo = (preset, mode, skipConfirm = false) => {
    const targets = scopeTargets(mode);
    if (!targets.size) { toast.error(mode === "all" ? "No captions on the timeline yet — create captions first." : "Select a caption first, or choose Apply to All."); return false; }
    if (mode === "all" && !skipConfirm && !window.confirm(`Apply "${preset.name}" to all ${targets.size} captions?`)) return;
    const next = layers.map((l) => ({ ...l, objects: (l.objects || []).map((o) => (targets.has(`${l.id}:${o.id}`) ? applyPresetToObject(o, preset) : o)) }));
    commit(next);
    // Two-font presets: apply the (global) fontMode/fontFamilyB so preview AND export
    // both render the secondary font. Single-font presets never touch config.
    const cfgPatch = presetConfigPatch(preset);
    const targetObjs = layers.flatMap((l) => (l.objects || []).filter((o) => targets.has(`${l.id}:${o.id}`)));
    // Several caption layers + a multi-look design: layer 1 gets look 1, layer 2 look 2, … (looping).
    const capLayersHit = orderedLayers.filter((l) => (l.objects || []).some((o) => targets.has(`${l.id}:${o.id}`)));
    const ws = capLayersHit.length > 1 && (preset.definition?.wordPattern || []).length > 1
      ? layerPatternPatch(capLayersHit.map((l) => (l.objects || []).filter((o) => targets.has(`${l.id}:${o.id}`))), preset.definition, captionConfig?.wordStyles || {}, captionConfig || {})
      : phrasePatternPatch(targetObjs, preset.definition, captionConfig?.wordStyles || {}, captionConfig || {}, sentenceEndIds);
    if (onConfigPatch) onConfigPatch({ ...(cfgPatch || {}), wordStyles: ws });
    toast.success(`Applied "${preset.name}" to ${targets.size} caption${targets.size > 1 ? "s" : ""}${capLayersHit.length > 1 && (preset.definition?.wordPattern || []).length > 1 ? ` across ${capLayersHit.length} layers` : ""}`);
    setPresetsOpen(false);
    return true;
  };
  const resetPreset = () => { const o = selObjs()[0]; if (!o?.preset) return; updateObj(sel.layerId, o.id, resetPresetPatch(o)); toast.success("Reset to preset"); };
  const detachPreset = () => { const o = selObjs()[0]; if (!o) return; updateObj(sel.layerId, o.id, { preset: null }); toast.success("Detached from preset"); };
  const saveAsPreset = async () => {
    const o = selObjs()[0]; if (!o) { toast.error("Select a caption first."); return; }
    const name = window.prompt("Save this design to My Text Presets — name:", o.preset?.name ? `${o.preset.name} copy` : "My Design");
    if (!name) return;
    const definition = definitionFromObject(o, captionConfig?.wordStyles || {}, captionConfig || {});
    try {
      await api.createPreset({ name, category: definition.wordPattern ? "phrase" : "caption", definition });
      setPresetsVersion((v) => v + 1);
      toast.success(`Saved "${name}" to My Text Presets`);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save preset"); }
  };
  // Drag an asset card from Project Media and drop it onto the timeline at the drop position.
  const onAssetDrop = (e) => {
    const id = e.dataTransfer.getData("asset-id");
    if (!id) return;
    e.preventDefault();
    const asset = (baseAsset && id === baseAsset.id) ? baseAsset : mediaAssets.find((a) => a.id === id);
    if (!asset) return;
    const rect = scrollRef.current?.getBoundingClientRect();
    const t = rect ? (e.clientX - rect.left + (scrollRef.current.scrollLeft || 0)) / pxPerSec : null;
    addAssetToTimeline(asset, t);
  };
  const renameAsset = (id, name) => onAssetsChange && onAssetsChange(mediaAssets.map((a) => a.id === id ? { ...a, name } : a));
  const deleteAsset = (id) => {
    // Remove the asset AND every timeline clip that references it, so Project Media,
    // the timeline and the preview never disagree (no orphaned clips / stale playback).
    const removed = (mediaTracks || []).reduce((n, t) => n + (t.objects || []).filter((o) => o.asset_id === id).length, 0);
    if (removed > 0 && !window.confirm(`This media is used by ${removed} clip${removed > 1 ? "s" : ""} on the timeline. Remove it from Project Media and delete those clip${removed > 1 ? "s" : ""}?`)) return;
    onAssetsChange && onAssetsChange(mediaAssets.filter((a) => a.id !== id));
    const cleaned = (mediaTracks || []).map((t) => ({ ...t, objects: (t.objects || []).filter((o) => o.asset_id !== id) }));
    if (removed > 0) {
      if (mediaSel) {
        const stillThere = cleaned.some((t) => t.id === mediaSel.trackId && (t.objects || []).some((o) => o.id === mediaSel.objId));
        if (!stillThere) setMediaSel(null);
      }
      onMediaChange && onMediaChange(cleaned);
    }
    toast.success(removed > 0 ? `Removed from Project Media and ${removed} timeline clip${removed > 1 ? "s" : ""}` : "Removed from Project Media");
  };


  // Duplicate selected words into ONE independent phrase object (own layer), placed after the range.
  const duplicatePhrase = () => {
    const objs = selObjs().slice().sort((a, b) => a.start - b.start);
    if (!objs.length) return;
    const s0 = objs[0].start, e0 = objs[objs.length - 1].end;
    const len = Math.max(0.4, e0 - s0);
    let ns = e0, ne = ns + len;
    if (ne > dur) { ne = dur; ns = Math.max(0, ne - len); }
    const words = objs.map((o) => ({
      word_id: o.word_id || (o.words && o.words[0] && o.words[0].word_id) || null,
      word: o.text, start: round(ns + Math.max(0, o.start - s0)), end: round(ns + Math.max(0.05, o.end - s0)),
    }));
    const phrase = { id: uid(), text: objs.map((o) => o.text).join(" "), start: round(ns), end: round(ne), words, style: objs[0].style || null, animation: objs[0].animation || null, timeLocked: true };
    const existing = layers.find((l) => l.id === "phrases-layer");
    const next = existing
      ? layers.map((l) => l.id === "phrases-layer" ? { ...l, objects: [...l.objects, phrase] } : l)
      : [...layers, { id: "phrases-layer", name: "Phrases", type: "text", locked: false, hidden: false, objects: [phrase] }];
    commit(next);
    setSel({ layerId: "phrases-layer", ids: [phrase.id] });
    toast.success(`Duplicated phrase "${phrase.text}"`);
  };

  // Split the selected caption at the playhead — using real word boundaries (never mid-word).
  const splitCaption = () => {
    const o = selObjs()[0];
    if (!o) return;
    const t = currentTime || 0;
    const ws = (o.words && o.words.length ? o.words : [{ word: o.text, start: o.start, end: o.end, word_id: o.word_id }]);
    const a = ws.filter((w) => w.start < t);
    const b = ws.filter((w) => w.start >= t);
    if (!a.length || !b.length) { toast.error("Move the playhead between two words of this caption to split."); return; }
    const mk = (part) => ({ id: uid(), text: part.map((w) => w.word).join(" "), start: round(Math.min(...part.map((w) => w.start))), end: round(Math.max(...part.map((w) => w.end))), words: part.map((w) => ({ ...w })), style: o.style || null, animation: o.animation || null, timeLocked: true, textLocked: o.textLocked });
    const parts = [mk(a), mk(b)];
    commit(layers.map((l) => l.id !== sel.layerId ? l : { ...l, objects: l.objects.flatMap((x) => x.id === o.id ? parts : [x]) }));
    setSel({ layerId: sel.layerId, ids: [parts[0].id] });
    toast.success("Caption split at playhead");
  };

  // Merge selected adjacent captions into one — earliest start, latest end, words preserved.
  const mergeCaptions = () => {
    const objs = selObjs().slice().sort((a, b) => a.start - b.start);
    if (objs.length < 2) return;
    // Part T: only allow merging CONSECUTIVE captions from the same track.
    const layer = layers.find((l) => l.id === sel.layerId);
    if (layer) {
      const ordered = layer.objects.slice().sort((a, b) => a.start - b.start);
      const ids = objs.map((o) => o.id);
      const idx = ordered.map((o, i) => (ids.includes(o.id) ? i : -1)).filter((i) => i >= 0);
      const contiguous = idx.length === ids.length && (idx[idx.length - 1] - idx[0] + 1) === idx.length;
      if (!contiguous) { toast.error("Please select consecutive captions to merge."); return; }
    }
    const allWords = objs.flatMap((o) => (o.words && o.words.length ? o.words : [{ word: o.text, start: o.start, end: o.end, word_id: o.word_id }]));
    allWords.sort((a, b) => a.start - b.start);
    const merged = { id: uid(), text: allWords.map((w) => w.word).join(" "), start: round(Math.min(...objs.map((o) => o.start))), end: round(Math.max(...objs.map((o) => o.end))), words: allWords.map((w) => ({ ...w })), style: objs[0].style || null, animation: objs[0].animation || null, timeLocked: true };
    const ids = objs.map((o) => o.id);
    commit(layers.map((l) => l.id !== sel.layerId ? l : { ...l, objects: [...l.objects.filter((x) => !ids.includes(x.id)), merged].sort((a, b) => a.start - b.start) }));
    setSel({ layerId: sel.layerId, ids: [merged.id] });
    toast.success("Captions merged");
  };

  // Move selected caption(s) to the adjacent caption/text layer.
  // dir = -1 → layer above (on top), dir = +1 → layer below. Only layer_id changes:
  // text / start / end / animation / style / word timing are all preserved.
  // Moving down past the bottom-most layer auto-creates a new caption layer.
  const moveSelToLayer = (dir) => {
    const objs = selObjs();
    if (!objs.length || !sel) return;
    const curLayer = layers.find((l) => l.id === sel.layerId);
    if (!curLayer || curLayer.locked) { if (curLayer?.locked) toast.error("This layer is locked"); return; }
    const ordIdx = orderedLayers.findIndex((l) => l.id === sel.layerId);
    if (ordIdx < 0) return;
    let target = orderedLayers[ordIdx + dir];
    let next = [...layers];
    let created = false;
    if (!target) {
      if (dir > 0) {
        const n = layers.length + 1;
        target = { id: uid(), name: `Captions ${n}`, type: "text", locked: false, hidden: false, objects: [] };
        next = [...next, target];
        created = true;
      } else { return; } // already at the top-most layer
    }
    if (target.locked) { toast.error("Target layer is locked"); return; }
    const ids = objs.map((o) => o.id);
    const moving = objs.map((o) => ({ ...o })); // preserve everything
    next = next.map((l) => {
      if (l.id === curLayer.id) return { ...l, objects: l.objects.filter((o) => !ids.includes(o.id)) };
      if (l.id === target.id) return { ...l, objects: [...l.objects, ...moving].sort((a, b) => a.start - b.start) };
      return l;
    });
    commit(next);
    setSel({ layerId: target.id, ids });
    toast.success(created ? "Moved to a new layer" : dir > 0 ? "Moved to the layer below" : "Moved to the layer above");
  };
  const canMoveUp = sel && orderedLayers.findIndex((l) => l.id === sel.layerId) > 0;

  // Time-move: shift selected caption object(s) by exactly ±1.000s (preserve duration).
  const nudgeCaptionTime = (dir) => {
    const objs = selObjs(); if (!objs.length || !sel) return;
    const ids = objs.map((o) => o.id);
    commit(layers.map((l) => l.id !== sel.layerId ? l : { ...l, objects: l.objects.map((o) => {
      if (!ids.includes(o.id)) return o;
      const len = (o.end || 0) - (o.start || 0);
      const ns = round(Math.max(0, (o.start || 0) + dir));
      return { ...o, start: ns, end: round(ns + len), timeLocked: true };
    }) }));
  };
  const captionCanBack = selObjs().every((o) => (o.start || 0) >= 1);

  // ── Move ONLY the selected individual OBJECT one z-position (index 0 = front) ─────────────
  // If its layer holds multiple objects, the object is EXTRACTED into its own adjacent layer so
  // only it moves. If it already owns its layer, that layer swaps position with the adjacent one
  // (single object at an edge going DOWN gets an empty slot; going UP into an empty slot absorbs
  // it — clean round-trip, never duplicated). Media: reorder the selected clip's track. The same
  // object (stable id) stays selected. This backs BOTH the keyboard ↑/↓ and the UI arrow buttons.
  const moveSelLayer = (dir) => {
    if (mediaSel) { moveMediaTrack(dir); return; }
    if (!sel || !sel.ids || sel.ids.length !== 1) return;
    const objId = sel.ids[0];
    const curIdx = layers.findIndex((l) => l.id === sel.layerId);
    if (curIdx < 0) return;
    const cur = layers[curIdx];
    if (cur.locked) { toast.error("This layer is locked"); return; }
    const obj = (cur.objects || []).find((o) => o.id === objId);
    if (!obj) return;
    const arr = [...layers];
    if ((cur.objects || []).length > 1) {
      // Extract just this object into its own layer placed one step in `dir`.
      const newLayer = { id: uid(), name: cur.name, type: cur.type || "text", locked: false, hidden: false, objects: [obj] };
      arr[curIdx] = { ...cur, objects: cur.objects.filter((o) => o.id !== objId) };
      arr.splice(dir < 0 ? curIdx : curIdx + 1, 0, newLayer);
      commit(arr);
      setSel({ layerId: newLayer.id, ids: [objId] });
      toast.success(dir < 0 ? "Moved up one layer" : "Moved down one layer");
      return;
    }
    const to = curIdx + dir;
    if (to >= 0 && to < arr.length) {
      if (dir === -1 && (arr[to].objects || []).length === 0) { arr.splice(to, 1); }
      else { const tmp = arr[curIdx]; arr[curIdx] = arr[to]; arr[to] = tmp; }
    } else if (dir === 1) {
      arr.splice(curIdx, 0, { id: uid(), name: "Layer", type: cur.type || "text", locked: false, hidden: false, objects: [] });
    } else { return; }
    commit(arr);
    toast.success(dir < 0 ? "Moved up one layer" : "Moved down one layer");
  };

  // ── Left/Right navigation across sibling objects ────────────────────────────────────────
  // Captions/text navigate by timeline order (start, then end, then stable id). Media navigate
  // within the same kind (video/image/element). Selection updates immediately + playhead moves
  // to the target's start so the ribbon/preview follow.
  const navigate = (dir) => {
    if (mediaSel) {
      const cur = mediaClip(); if (!cur) return;
      const kind = cur.kind;
      const flat = [];
      mediaTracks.forEach((tr) => (tr.objects || []).forEach((o) => { if ((o.kind || tr.type) === kind) flat.push({ trackId: tr.id, o }); }));
      flat.sort((a, b) => (a.o.start || 0) - (b.o.start || 0) || (a.o.end || 0) - (b.o.end || 0) || String(a.o.id).localeCompare(String(b.o.id)));
      const i = flat.findIndex((x) => x.o.id === cur.id);
      const nx = flat[i + dir]; if (!nx) return;
      setMediaSel({ trackId: nx.trackId, objId: nx.o.id }); setSel(null);
      onSeek?.(nx.o.start || 0);
      return;
    }
    const flat = [];
    layers.forEach((l) => { if (l.type === "captions" || l.type === "text") (l.objects || []).forEach((o) => flat.push({ layerId: l.id, o })); });
    flat.sort((a, b) => (a.o.start || 0) - (b.o.start || 0) || (a.o.end || 0) - (b.o.end || 0) || String(a.o.id).localeCompare(String(b.o.id)));
    if (!flat.length) return;
    const curId = sel?.ids?.[0];
    let i = flat.findIndex((x) => x.o.id === curId);
    if (i < 0) i = dir > 0 ? -1 : flat.length;
    const nx = flat[i + dir]; if (!nx) return;
    setSel({ layerId: nx.layerId, ids: [nx.o.id] });
    onSeek?.(nx.o.start || 0);
  };

  // Media: reorder the selected clip's TRACK (vertical layer order → composite/render order).
  const moveMediaTrack = (dir) => {
    if (!mediaSel) return;
    const i = mediaTracks.findIndex((t) => t.id === mediaSel.trackId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= mediaTracks.length) return;
    const next = [...mediaTracks];
    [next[i], next[j]] = [next[j], next[i]];
    commitMedia(next);
  };
  // Media: shift the selected clip (and its linked partner) by exactly ±1.000s.
  const nudgeMediaTime = (dir) => {
    const clip = mediaClip(); if (!clip) return;
    const linkId = clip.linked_id;
    commitMedia(mediaTracks.map((tr) => ({ ...tr, objects: tr.objects.map((o) => {
      if (o.id === clip.id || (linkId && o.id === linkId)) {
        const len = (o.end || 0) - (o.start || 0);
        const ns = round(Math.max(0, (o.start || 0) + dir));
        return { ...o, start: ns, end: round(ns + len) };
      }
      return o;
    }) })));
  };

  const copyStyle = () => { const o = selObjs()[0]; setCopiedStyle({ style: o?.style || {}, animation: o?.animation || null }); toast.success("Style copied"); };
  const pasteStyle = () => {
    if (!copiedStyle || !sel) { toast.error("Copy a style first"); return; }
    commit(layers.map((l) => l.id !== sel.layerId ? l : { ...l, objects: l.objects.map((o) => sel.ids.includes(o.id) ? { ...o, style: copiedStyle.style, animation: copiedStyle.animation } : o) }));
    toast.success("Style pasted");
  };

  // Convert any click's screen X into a timeline time and seek there (clicking a clip must
  // move the playhead to the clicked position, NOT the clip's first frame).
  const seekFromClientX = (clientX) => {
    if (!scrollRef.current) return 0;
    const rect = scrollRef.current.getBoundingClientRect();
    const x = clientX - rect.left + scrollRef.current.scrollLeft;
    const t = Math.max(0, Math.min(dur, x / pxPerSec));
    onSeek?.(t);
    if (isPlaying && autoFollow) setAutoFollow(false);
    return t;
  };
  const scrubRef = useRef(false);
  const seekFromX = (e) => {
    if (scrubRef.current) return; // a playhead drag just happened — don't double-seek/deselect
    const rect = scrollRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left + scrollRef.current.scrollLeft;
    onSeek?.(Math.max(0, Math.min(dur, x / pxPerSec)));
    setSel(null);
    if (isPlaying && autoFollow) setAutoFollow(false); // user scrubbed the timeline
  };

  // ---- Draggable PLAYHEAD grip: continuous scrub. ONE canonical time via onSeek(t). ----
  const startPlayheadDrag = (e) => {
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    scrubRef.current = true;
    if (isPlaying) { onPause?.(); if (autoFollow) setAutoFollow(false); }
    const timeAt = (clientX) => {
      const r = scrollRef.current.getBoundingClientRect();
      const edge = 44;
      if (clientX > r.right - edge) scrollRef.current.scrollLeft += 14;
      else if (clientX < r.left + edge) scrollRef.current.scrollLeft = Math.max(0, scrollRef.current.scrollLeft - 14);
      const x = clientX - r.left + scrollRef.current.scrollLeft;
      return Math.max(0, Math.min(dur, x / pxPerSec));
    };
    onSeek?.(timeAt(e.clientX));
    const move = (ev) => { ev.preventDefault(); onSeek?.(timeAt(ev.clientX)); };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      setTimeout(() => { scrubRef.current = false; }, 0); // swallow the trailing click
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const Block = ({ layer, obj }) => {
    const left = obj.start * pxPerSec;
    const w = Math.max(6, (obj.end - obj.start) * pxPerSec);
    const selected = sel?.layerId === layer.id && sel.ids.includes(obj.id);
    const isPhrase = Array.isArray(obj.words) && obj.words.length > 1;
    return (
      <div data-testid="caption-block" onPointerDown={(e) => blockPointerDown(e, layer, obj, "move")} onContextMenu={(e) => window.matchMedia("(pointer: coarse)").matches && e.preventDefault()} onClick={(e) => clickBlock(e, layer, obj)}
        data-picked={picked.has(`${layer.id}:${obj.id}`) ? "true" : undefined}
        className={`absolute top-1.5 flex h-9 cursor-grab items-center overflow-hidden rounded-md border px-2 text-[11px] font-semibold text-white transition-colors ${picked.has(`${layer.id}:${obj.id}`) ? "outline outline-2 outline-offset-1 outline-[#FFC94D]" : ""} ${selected ? "border-[#00F0FF] bg-[#00F0FF]/25 ring-1 ring-[#00F0FF]" : isPhrase ? "border-[#00F0FF]/30 bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30" : "border-white/20 bg-[#8B5CF6]/45 hover:bg-[#8B5CF6]/60"}`}
        style={{ left, width: w, WebkitTouchCallout: "none" }} title={`${obj.text} (${obj.start.toFixed(2)}–${obj.end.toFixed(2)}s)`}>
        {!layer.locked && <span onPointerDown={(e) => startDrag(e, layer, obj, "left")} data-testid="caption-handle-left" className="absolute left-0 top-0 h-full w-1.5 cursor-ew-resize bg-white/40" />}
        <span className="truncate px-1">{obj.text}</span>
        {!layer.locked && <span onPointerDown={(e) => startDrag(e, layer, obj, "right")} data-testid="caption-handle-right" className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize bg-white/40" />}
      </div>
    );
  };

  const RefTrack = ({ children }) => <div className="relative border-b border-white/5" style={{ height: ROW_H, width: innerW }}>{children}</div>;

  // ---- Virtualization (Phase 13/37): only render blocks intersecting the viewport (+ buffer) ----
  const viewStartT = scrollLeft / pxPerSec;
  const viewEndT = (scrollLeft + viewW) / pxPerSec;
  const bufT = (viewW * 0.5) / pxPerSec;
  const inView = (o) => (o.end >= viewStartT - bufT && o.start <= viewEndT + bufT);

  // ---- Batch 3: waveform canvas + media clip block ----
  const WaveCanvas = ({ peaks, url, ss, se, srcDur, width, height, muted }) => {
    const ref = useRef(null);
    const [fetched, setFetched] = useState(null);
    useEffect(() => {
      if (!url) return;
      let alive = true;
      getWaveform(url).then((w) => { if (alive) setFetched(w); }).catch(() => {});
      return () => { alive = false; };
    }, [url]);
    const pk = url ? (fetched?.peaks || []) : peaks;
    const sdur = url ? (fetched?.duration || srcDur) : srcDur;
    useEffect(() => {
      const cv = ref.current; if (!cv) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = Math.max(1, Math.round(width * dpr)); cv.height = Math.round(height * dpr);
      const ctx = cv.getContext("2d"); ctx.clearRect(0, 0, cv.width, cv.height);
      if (!pk || !pk.length || sdur <= 0) return;
      const N = pk.length;
      const i0 = Math.max(0, Math.floor((ss / sdur) * N));
      const i1 = Math.min(N, Math.ceil((se / sdur) * N));
      const span = Math.max(1, i1 - i0);
      const mid = cv.height / 2;
      ctx.fillStyle = muted ? "rgba(255,255,255,0.18)" : "#00F0FF";
      const bars = Math.min(Math.round(cv.width), 1200);
      for (let b = 0; b < bars; b++) {
        const idx = i0 + Math.floor((b / bars) * span);
        const v = pk[idx] || 0;
        const h = Math.max(1, v * mid * 0.95);
        const x = (b / bars) * cv.width;
        ctx.fillRect(x, mid - h, Math.max(1, cv.width / bars), h * 2);
      }
    }, [pk, sdur, ss, se, width, height, muted]);
    return <canvas ref={ref} style={{ width, height }} className="pointer-events-none absolute inset-0 opacity-75" />;
  };

  const MediaBlock = ({ track, obj }) => {
    const [clipThumbs, setClipThumbs] = useState(null);
    useEffect(() => {
      if (track.type !== "video" || !obj.asset_url) return;
      let a = true; getVideoThumbnails(obj.asset_url, 12).then((t) => a && setClipThumbs(t)).catch(() => {});
      return () => { a = false; };
    }, [obj.asset_url, track.type]);
    const left = obj.start * pxPerSec;
    const w = Math.max(8, (obj.end - obj.start) * pxPerSec);
    const selected = mediaSel?.trackId === track.id && mediaSel?.objId === obj.id;
    const ss = obj.source_start ?? 0, se = obj.source_end ?? obj.end;
    const isVideo = track.type === "video";
    const isElement = track.type === "element";
    const tset = obj.asset_url ? clipThumbs : thumbs;
    const vis = (tset?.thumbs || []).filter((th) => th.t >= ss - 0.05 && th.t <= se + 0.05);
    if (isElement) {
      return (
        <div data-testid={`media-clip-${obj.id}`} onPointerDown={(e) => startMediaDrag(e, track, obj, "move")}
          onClick={(e) => { e.stopPropagation(); setMediaSel({ trackId: track.id, objId: obj.id }); seekFromClientX(e.clientX); }}
          className={`absolute top-1 flex h-[38px] cursor-grab items-center gap-1.5 overflow-hidden rounded-md border px-2 ${selected ? "border-[#00F0FF] ring-1 ring-[#00F0FF]" : "border-[#EC4899]/40"} bg-[#EC4899]/15`}
          style={{ left, width: w }} title={`Element ${(obj.element || "shape")} ${obj.start.toFixed(2)}–${obj.end.toFixed(2)}s`}>
          {!track.locked && <span onPointerDown={(e) => startMediaDrag(e, track, obj, "left")} data-testid={`media-handle-left-${obj.id}`} className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-ew-resize bg-[#EC4899]/70" />}
          <Shapes size={12} className="shrink-0 text-[#EC4899]" />
          <span className="pointer-events-none truncate text-[10px] font-semibold capitalize text-white">{obj.element || "shape"}</span>
          {!track.locked && <span onPointerDown={(e) => startMediaDrag(e, track, obj, "right")} data-testid={`media-handle-right-${obj.id}`} className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-ew-resize bg-[#EC4899]/70" />}
        </div>
      );
    }
    return (
      <div data-testid={`media-clip-${obj.id}`} onPointerDown={(e) => startMediaDrag(e, track, obj, "move")}
        onClick={(e) => { e.stopPropagation(); setMediaSel({ trackId: track.id, objId: obj.id }); seekFromClientX(e.clientX); }}
        className={`absolute top-1 flex h-[38px] cursor-grab items-center overflow-hidden rounded-md border ${selected ? "border-[#00F0FF] ring-1 ring-[#00F0FF]" : "border-white/15"} ${isVideo ? "bg-[#1b1b1b]" : "bg-[#122a2e]"}`}
        style={{ left, width: w }} title={`${isVideo ? "Video" : "Audio"} ${obj.start.toFixed(2)}–${obj.end.toFixed(2)}s`}>
        {isVideo ? (
          <div className="pointer-events-none absolute inset-0 flex overflow-hidden opacity-85">
            {vis.map((th, i) => <img key={i} src={th.url} alt="" draggable={false} style={{ position: "absolute", left: (i / vis.length) * w, width: w / Math.max(1, vis.length) + 1, height: "100%" }} className="object-cover" />)}
          </div>
        ) : (
          <WaveCanvas peaks={obj.asset_url ? undefined : wave?.peaks} url={obj.asset_url} ss={ss} se={se} srcDur={obj.asset_duration || wave?.duration || dur} width={w} height={38} muted={obj.muted || track.muted} />
        )}
        {!track.locked && <span onPointerDown={(e) => startMediaDrag(e, track, obj, "left")} data-testid={`media-handle-left-${obj.id}`} className="absolute left-0 top-0 z-10 h-full w-1.5 cursor-ew-resize bg-[#00F0FF]/70" />}
        {(obj.muted || track.muted) && <span className="absolute right-2 top-1 z-10 text-[#ff6b6b]"><VolumeX size={12} /></span>}
        {!track.locked && <span onPointerDown={(e) => startMediaDrag(e, track, obj, "right")} data-testid={`media-handle-right-${obj.id}`} className="absolute right-0 top-0 z-10 h-full w-1.5 cursor-ew-resize bg-[#00F0FF]/70" />}
        <span className="pointer-events-none relative z-[5] ml-2 rounded bg-black/50 px-1 text-[10px] font-semibold text-white">{(obj.end - obj.start).toFixed(1)}s</span>
      </div>
    );
  };

  const mediaLabelCell = (track) => {
    const isAudio = track.type === "audio";
    const isElement = track.type === "element";
    const { linked } = linkedPair();
    return (
      <div className="flex shrink-0 flex-col justify-center gap-1 border-b border-white/5 bg-[#0d0d0d] px-3" style={{ height: ROW_H, width: LABEL_W }} data-testid={`media-label-${track.type}`}>
        <div className="flex items-center gap-1 truncate text-[11px] font-semibold text-white">
          {isElement ? <Shapes size={12} className="text-[#EC4899]" /> : isAudio ? <AudioLines size={12} className="text-[#00F0FF]" /> : <Video size={12} className="text-[#8B5CF6]" />}
          <span className="truncate">{track.name}</span>
          {linked && <Link2 size={11} className="shrink-0 text-[#00F0FF]" />}
        </div>
        <div className="flex items-center gap-1.5 text-[#A1A1AA]">
          <button onClick={() => patchMediaTrack(track.id, { hidden: !track.hidden })} data-testid={`media-hide-${track.type}`} title="Hide">{track.hidden ? <EyeOff size={12} /> : <Eye size={12} />}</button>
          <button onClick={() => patchMediaTrack(track.id, { locked: !track.locked })} data-testid={`media-lock-${track.type}`} title="Lock">{track.locked ? <Lock size={12} /> : <Unlock size={12} />}</button>
          <button onClick={() => patchMediaTrack(track.id, { muted: !track.muted })} data-testid={`media-mute-${track.type}`} title="Mute">{track.muted ? <VolumeX size={12} className="text-[#ff6b6b]" /> : <Volume2 size={12} />}</button>
          {(track.id === "track-video" || track.id === "track-audio") && <button onClick={toggleLink} data-testid="media-link-toggle" title={linked ? "Unlink audio from video" : "Relink audio to video"} className="hover:text-[#00F0FF]">{linked ? <Unlink size={12} /> : <Link2 size={12} />}</button>}
        </div>
      </div>
    );
  };

  const mediaTrackRow = (track) => (
    <div key={track.id} className={`relative border-b border-white/5 ${track.hidden ? "opacity-30" : ""}`} style={{ height: ROW_H, width: innerW }} data-testid={`media-track-${track.type}`}>
      {track.type === "audio" && beatsOn && (wave?.beats || []).map((bt, i) => <div key={i} className="pointer-events-none absolute bottom-0 top-0 z-[1] w-px bg-[#FFC53D]/40" style={{ left: bt * pxPerSec }} />)}
      {isFeatureEnabled("gap_selection") && trackGaps(track).map((g, i) => {
        const isSel = gapSel && gapSel.trackId === track.id && Math.abs((gapSel.start || 0) - g.start) < 1e-3;
        return <div key={`gap-${i}`} data-testid={`timeline-gap-${track.type}-${i}`}
          onClick={(e) => { e.stopPropagation(); setGapSel({ trackId: track.id, start: g.start, end: g.end }); setMediaSel(null); }}
          title="Empty gap — press Delete to close"
          className={`absolute top-1 z-[2] cursor-pointer rounded-sm border border-dashed transition-colors ${isSel ? "border-[#ff6b6b] bg-[#ff6b6b]/25" : "border-white/15 bg-white/[0.03] hover:bg-white/[0.07]"}`}
          style={{ left: g.start * pxPerSec, width: Math.max(2, (g.end - g.start) * pxPerSec), height: ROW_H - 8 }} />;
      })}
      {(track.objects || []).map((o) => <MediaBlock key={o.id} track={track} obj={o} />)}
    </div>
  );

  const mediaClipSel = mediaClip();

  const selectedObjs = selObjs();
  const single = selectedObjs.length === 1 ? selectedObjs[0] : null;
  // live=true (slider drag): only the first tick records an undo step.
  const liveDragRef = useRef(false);
  const patchSelected = (patch, live = false) => {
    if (!sel && !picked.size) return;
    const keys = picked.size ? picked : new Set(sel.ids.map((id) => `${sel.layerId}:${id}`));
    const next = layers.map((l) => ({ ...l, objects: (l.objects || []).map((o) => (keys.has(`${l.id}:${o.id}`) ? { ...o, ...patch } : o)) }));
    (live && liveDragRef.current ? onChange : commit)(next);
    liveDragRef.current = live;
  };
  // Follow the playhead: select the caption under it (playing or scrubbing), unless the user is typing/styling.
  const followTRef = useRef(currentTime);
  const followRef = useRef(() => {});
  useEffect(() => {
    // Catch up once focus leaves an input or a dialog closes.
    const onOut = () => setTimeout(() => followRef.current(), 60);
    window.addEventListener("focusout", onOut);
    return () => window.removeEventListener("focusout", onOut);
  }, []);
  followRef.current = () => {
    if (pickMode || mediaSel || (sel && sel.ids.length > 1)) return;
    const ae = document.activeElement;
    if (ae && (/^(INPUT|TEXTAREA|SELECT)$/.test(ae.tagName) || ae.isContentEditable)) return;
    if (document.querySelector('[role="dialog"]')) return;
    const caps = layers.filter((l) => l.type === "captions" || l.type === "text");
    const order = sel ? [...caps.filter((l) => l.id === sel.layerId), ...caps.filter((l) => l.id !== sel.layerId)] : caps;
    for (const l of order) {
      const o = (l.objects || []).find((x) => currentTime >= (x.start || 0) && currentTime < (x.end || 0));
      if (o) { if (!(sel && sel.layerId === l.id && sel.ids[0] === o.id)) setSel({ layerId: l.id, ids: [o.id] }); return; }
    }
  };
  useEffect(() => {
    if (Math.abs(currentTime - followTRef.current) < 1e-3) return;
    followTRef.current = currentTime;
    followRef.current();
  }, [currentTime]);
  const selLayer = sel && layers.find((l) => l.id === sel.layerId);
  const phraseText = selectedObjs.map((o) => o.text).join(" ");

  // Register high-level actions for the top ribbon (Editor drives them via a ref).
  useEffect(() => {
    if (!actionsRef) return;
    actionsRef.current = {
      addVideo: () => videoInputRef.current?.click(),
      addImage: () => imageInputRef.current?.click(),
      addAudio: () => audioInputRef.current?.click(),
      recordVoice: () => startRec(),
      addText: () => setTwoLineOpen(true),
      undo, redo,
      applyPresetAll: (p) => applyPresetTo(p, "all", true),
      applyPresetScoped: (p, mode) => applyPresetTo(p, mode, true),
      scopeInfo,
      canUndo: () => histRef.current.length > 0,
      canRedo: () => redoHistRef.current.length > 0,
      addCaptionLayer: () => addLayer(),
      createCaptions: () => createCaptions(),
      addElement: (t) => addElement(t),
      uploadMedia: () => uploadMediaInputRef.current?.click(),
      // Video Shape + Background Canvas (driven from the top ribbon popups).
      getSelectedVideo: () => {
        const c = mediaClip();
        return c && mediaSel ? { trackId: mediaSel.trackId, objId: mediaSel.objId, clip: c } : null;
      },
      patchSelectedVideo: (patch) => { if (mediaSel) patchMediaObj(mediaSel.trackId, mediaSel.objId, patch); },
      split: () => { if (mediaSel) splitMediaAtPlayhead(); else if (selObjs().length) splitCaption(); },
      duplicate: () => { if (mediaSel) duplicateMediaClip(); else if (selObjs().length) duplicatePhrase(); },
      zoomIn: () => applyZoom(zoom * 1.25),
      zoomOut: () => applyZoom(zoom / 1.25),
      nudge: (dir) => { if (mediaSel) nudgeMediaTime(dir); else nudgeCaptionTime(dir); },
      copyStyle: () => { if (selObjs().length) copyStyle(); },
      pasteStyle: () => { if (selObjs().length) pasteStyle(); },
      copy: () => { const objs = selObjs(); if (!objs.length) return false; clipRef.current = { layerId: sel.layerId, objs }; toast.success(`Copied ${objs.length} caption${objs.length > 1 ? "s" : ""}`); return true; },
      paste: () => {
        const c = clipRef.current; if (!c) return;
        const lid = layers.some((l) => l.id === sel?.layerId) ? sel.layerId : c.layerId;
        const t0 = Math.min(...c.objs.map((o) => o.start || 0)), at = currentTime || 0;
        const fresh = c.objs.map((o) => ({ ...o, id: uid(), start: round(at + (o.start - t0)), end: round(at + (o.end - t0)),
          ...(o.words ? { words: o.words.map((w) => ({ ...w, start: round(at + (w.start - t0)), end: round(at + (w.end - t0)) })) } : {}) }));
        commit(layers.map((l) => (l.id === lid ? { ...l, objects: [...(l.objects || []), ...fresh].sort((a, b) => a.start - b.start) } : l)));
        setSel({ layerId: lid, ids: fresh.map((o) => o.id) });
      },
      selectAll: () => { const l = layers.find((x) => x.id === sel?.layerId) || orderedLayers.find((x) => (x.objects || []).length); if (l) setSel({ layerId: l.id, ids: (l.objects || []).map((o) => o.id) }); },
      deselect: () => { setSel(null); setMediaSel(null); },
      moveLayerUp: () => moveSelLayer(-1),
      moveLayerDown: () => moveSelLayer(1),
      selectNext: () => navigate(1),
      selectPrev: () => navigate(-1),
      hasSelection: () => !!(mediaSel || (sel && sel.ids && sel.ids.length)),
      deleteSelection: () => {
        if (selectedObjs.length > 0 && sel) { deleteObjs(sel.layerId, sel.ids); return; }
        if (mediaSel) { commitMedia(mediaTracks.map((tr) => tr.id !== mediaSel.trackId ? tr : { ...tr, objects: tr.objects.filter((o) => o.id !== mediaSel.objId) }).filter((tr) => (tr.type !== "element" && tr.type !== "image") || (tr.objects || []).length)); setMediaSel(null); }
      },
    };
  });

  // Report the current selection KIND to the Editor so the ribbon can highlight contextually.
  useEffect(() => {
    if (!onSelectionKind) return;
    if (selectedObjs.length > 0) { onSelectionKind("caption"); return; }
    if (mediaClipSel) { onSelectionKind(mediaClipSel.kind === "video" ? "video" : mediaClipSel.kind === "audio" ? "audio" : mediaClipSel.kind === "image" ? "image" : mediaClipSel.kind === "element" ? "element" : null); return; }
    onSelectionKind(null);
  }, [selectedObjs.length, mediaClipSel, onSelectionKind]);
  const canSplit = !!single && (single.words?.length || 0) > 1;

  const labelCell = (icon, name, layer) => {
    const idx = layer ? layers.findIndex((l) => l.id === layer.id) : -1;
    return (
    <div className="flex shrink-0 flex-col justify-center gap-1 border-b border-white/5 bg-[#0d0d0d] px-3" style={{ height: ROW_H, width: LABEL_W }}>
      <div className="flex items-center gap-1 truncate text-[11px] font-semibold text-white">
        {layer && <GripVertical size={12} className="shrink-0 cursor-grab text-[#555] hover:text-[#00F0FF]" data-testid="layer-drag-handle" />}
        {icon}
        {layer && renaming?.id === layer.id ? (
          <input autoFocus value={renaming.value} data-testid={`layer-rename-input-${layer.id}`}
            onChange={(e) => setRenaming({ id: layer.id, value: e.target.value })}
            onBlur={commitRename}
            onKeyDown={(e) => { if (e.key === "Enter") commitRename(); if (e.key === "Escape") setRenaming(null); }}
            className="w-full min-w-0 rounded border border-[#00F0FF]/50 bg-black/40 px-1 py-0.5 text-[11px] text-white outline-none" />
        ) : (
          <span className={`truncate ${layer ? "cursor-text" : ""}`} onDoubleClick={layer ? () => startRename(layer) : undefined} data-testid={layer ? `layer-name-${layer.id}` : undefined} title={layer ? "Double-click to rename" : undefined}>{name}</span>
        )}
      </div>
      {layer && (
        <div className="flex items-center gap-1.5 text-[#A1A1AA]">
          <button onClick={() => moveLayer(layer.id, -1)} disabled={idx <= 0} data-testid="layer-move-up" title="Move up (higher = on top)" className="hover:text-white disabled:opacity-30"><ChevronUp size={12} /></button>
          <button onClick={() => moveLayer(layer.id, 1)} disabled={idx >= layers.length - 1} data-testid="layer-move-down" title="Move down" className="hover:text-white disabled:opacity-30"><ChevronDown size={12} /></button>
          <button onClick={() => patchLayer(layer.id, { hidden: !layer.hidden })} data-testid="layer-hide" title="Hide">{layer.hidden ? <EyeOff size={12} /> : <Eye size={12} />}</button>
          <button onClick={() => patchLayer(layer.id, { locked: !layer.locked })} data-testid="layer-lock" title="Lock">{layer.locked ? <Lock size={12} /> : <Unlock size={12} />}</button>
          <button onClick={() => duplicateLayer(layer)} data-testid="layer-duplicate" title="Duplicate layer" className="hover:text-white"><Copy size={12} /></button>
          <button onClick={() => startRename(layer)} data-testid={`layer-rename-${layer.id}`} title="Rename layer" className="hover:text-white"><Pencil size={12} /></button>
          <button onClick={() => requestDeleteLayer(layer)} data-testid="layer-delete" title="Delete layer" className="hover:text-red-400"><Trash2 size={12} /></button>
        </div>
      )}
    </div>
  );
  };

  const hasAny = orderedLayers.some((l) => (l.objects || []).length);

  // Project Media list = canonical base video first, then uploaded/pasted assets.
  const displayAssets = baseAsset ? [baseAsset, ...mediaAssets.filter((a) => a.id !== baseAsset.id)] : mediaAssets;

  // ---- NLE Project Media panel (portaled into Editor's left column) ----
  const fmtBytes = (b) => (!b ? "" : b >= 1e9 ? `${(b / 1e9).toFixed(1)}GB` : b >= 1e6 ? `${(b / 1e6).toFixed(1)}MB` : `${Math.round(b / 1e3)}KB`);
  const MEDIA_TABS = [["all", "All"], ["video", "Videos"], ["image", "Images"], ["audio", "Audio"], ["elements", "Elements"]];
  const filteredAssets = mediaTab === "all" || mediaTab === "elements"
    ? displayAssets
    : displayAssets.filter((a) => (a.kind || "video") === mediaTab);
  const mediaPanelContent = (
    <div className="flex h-full flex-col" data-testid="project-media-panel">
      <div className="flex items-center justify-between border-b border-white/10 px-3 py-2.5">
        <span className="font-display text-[11px] font-bold uppercase tracking-wider text-white">Project Media</span>
        {uploading && <Loader2 size={13} className="animate-spin text-[#00F0FF]" />}
      </div>
      <input type="file" multiple accept="video/*,image/*,audio/*,.mp4,.mov,.webm,.m4v,.png,.jpg,.jpeg,.webp,.gif,.mp3,.wav,.m4a,.aac,.ogg" ref={uploadMediaInputRef} className="hidden" data-testid="pm-upload-media-input"
        onChange={(e) => { const fs = e.target.files; e.target.value = ""; uploadToLibrary(fs); }} />
      <div className="grid grid-cols-2 gap-1.5 border-b border-white/10 p-2">
        <button onClick={() => uploadMediaInputRef.current?.click()} disabled={uploading} data-testid="pm-upload-media-btn" className="flex items-center justify-center gap-1.5 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-2 py-2 text-[11px] font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-50">{uploading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Upload</button>
        <button onClick={pasteFromClipboard} disabled={uploading} data-testid="pm-paste-clipboard-btn" className="flex items-center justify-center gap-1.5 rounded-lg border border-white/15 px-2 py-2 text-[11px] font-semibold text-white hover:border-white/30 disabled:opacity-50"><ClipboardPaste size={13} /> Paste</button>
        <button onClick={() => setYtKind("video")} data-testid="pm-yt-video-btn" className="flex items-center justify-center gap-1.5 rounded-lg border border-[#FF3B3B]/40 px-2 py-2 text-[11px] font-semibold text-[#ff7a7a] hover:bg-[#FF3B3B]/10"><Video size={13} /> YouTube video</button>
        <button onClick={() => setYtKind("audio")} data-testid="pm-yt-audio-btn" className="flex items-center justify-center gap-1.5 rounded-lg border border-[#FF3B3B]/40 px-2 py-2 text-[11px] font-semibold text-[#ff7a7a] hover:bg-[#FF3B3B]/10"><Music size={13} /> YouTube audio</button>
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-white/10 px-2 py-1.5" data-testid="pm-tabs">
        {MEDIA_TABS.map(([v, l]) => (
          <button key={v} data-testid={`left-media-tab-${v}`} onClick={() => setMediaTab(v)}
            className={`shrink-0 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors ${mediaTab === v ? "bg-[#1E1E24] text-[#00F0FF]" : "text-[#A1A1AA] hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {mediaTab === "elements" ? (
        <div className="min-h-0 flex-1 overflow-y-auto p-2" data-testid="pm-elements-grid">
          <p className="px-1 pb-2 text-[10px] text-[#666]">Click to add a shape to the canvas at the playhead.</p>
          <div className="grid grid-cols-2 gap-1.5">
            {ELEMENT_TYPES.map((el) => (
              <button key={el.v} data-testid={`pm-element-${el.v}`} onClick={() => addElement(el.v)}
                className="flex flex-col items-center gap-1.5 rounded-lg border border-white/10 bg-[#141418] px-2 py-3 text-[10px] font-semibold text-white transition-colors hover:border-[#EC4899]/60 hover:bg-[#1E1E24]">
                <Shapes size={18} className="text-[#EC4899]" /> {el.l}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto p-2">
          {filteredAssets.length === 0 && <p className="px-2 py-6 text-center text-[11px] leading-relaxed text-[#666]">No media here yet.<br />Upload or paste media, then drag it onto the timeline.</p>}
          {filteredAssets.map((a, i) => {
            const isImg = a.kind === "image";
            const isAud = a.kind === "audio";
            return (
              <div key={a.id} draggable onDragStart={(e) => { try { e.dataTransfer.setData("asset-id", a.id); e.dataTransfer.effectAllowed = "copy"; } catch { /* noop */ } }}
                data-testid={`left-media-card-${i}`}
                className={`group relative overflow-hidden rounded-lg border p-1.5 active:cursor-grabbing ${a.is_base ? "border-[#00F0FF]/30 bg-[#00F0FF]/[0.04]" : "border-white/10 bg-[#141418] hover:border-[#00F0FF]/50"} cursor-grab transition-colors`}>
                <div className="relative flex aspect-video items-center justify-center overflow-hidden rounded bg-black/50">
                  {isImg ? (
                    <img src={absUrl(a.url)} alt="" draggable={false} className="h-full w-full object-cover" />
                  ) : isAud ? (
                    <AudioLines size={26} className="text-[#00F0FF]/70" />
                  ) : (
                    <Video size={26} className="text-[#8B5CF6]/70" />
                  )}
                  {!!(a.duration) && <span className="absolute bottom-1 right-1 rounded bg-black/80 px-1 font-plex text-[9px] text-white">{Math.round(a.duration)}s</span>}
                  {a.width && a.height && <span className="absolute left-1 top-1 rounded border border-[#00F0FF]/30 bg-[#00F0FF]/20 px-1 font-plex text-[8px] text-[#00F0FF]">{a.width}×{a.height}</span>}
                  {a.is_base && <span className="absolute left-1 top-1 rounded bg-[#00F0FF] px-1 text-[8px] font-bold text-black">MAIN</span>}
                </div>
                <div className="mt-1.5 flex items-center justify-between gap-1">
                  <div className="min-w-0 flex-1">
                    {a.is_base ? (
                      <span className="block truncate text-[11px] font-medium text-white" title="Main project video">{a.name}</span>
                    ) : (
                      <input value={a.name || ""} onChange={(e) => renameAsset(a.id, e.target.value)} data-testid={`media-asset-name-${a.id}`}
                        className="w-full truncate rounded bg-transparent text-[11px] font-medium text-white outline-none focus:bg-white/5" />
                    )}
                    <span className="text-[9px] text-[#666]">{a.is_base ? "main video" : a.kind}{a.size ? ` · ${fmtBytes(a.size)}` : ""}</span>
                  </div>
                  <button onClick={() => addAssetToTimeline(a)} data-testid={`media-asset-add-${a.id}`} title="Add to timeline" className="shrink-0 rounded p-1 text-[#A1A1AA] hover:text-[#00F0FF]"><Plus size={13} /></button>
                  {!a.is_base && <button onClick={() => deleteAsset(a.id)} data-testid={`media-asset-delete-${a.id}`} title="Remove from Project Media" className="shrink-0 rounded p-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={13} /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const projectSettingsPanel = (
    <div className="space-y-4 p-4" data-testid="inspector-project-settings">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-[#A1A1AA]">Project Settings</h3>
      <div className="space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Resolution</span><span className="font-plex tabular-nums text-white">{projectMeta?.width || "—"}×{projectMeta?.height || "—"}</span></div>
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Frame rate</span><span className="font-plex tabular-nums text-white">{Math.round(FPS)} fps</span></div>
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Duration</span><span className="font-plex tabular-nums text-white">{fmtMs(dur)}</span></div>
        <div className="flex justify-between"><span className="text-[#A1A1AA]">Aspect</span><span className="font-plex tabular-nums text-white">{projectMeta?.aspect || "9:16"}</span></div>
      </div>
      <p className="text-xs leading-relaxed text-[#666]">Select a caption or a clip on the timeline to edit its Properties, Animation, Transform and Audio.</p>
    </div>
  );

  // ---- Track groups (CAPTIONS → IMAGES → VIDEO → AUDIO) ----
  const imageTracks = mediaTracks.filter((t) => t.type === "image");
  const elementTracks = mediaTracks.filter((t) => t.type === "element");
  const videoTracks = mediaTracks.filter((t) => t.type === "video");
  const audioTracks = mediaTracks.filter((t) => t.type === "audio");
  const GROUPS = [
    { key: "captions", title: "Captions", icon: <Type size={12} className="text-[#8B5CF6]" />, addTitle: "Add caption layer", onAdd: addLayer, kind: "caption", items: orderedLayers, empty: "No captions yet — click Create Captions" },
    { key: "elements", title: "Elements", icon: <Shapes size={12} className="text-[#EC4899]" />, addTitle: "Add element", onAdd: () => addElement("rect"), kind: "media", items: elementTracks, empty: "Add shapes/lines from the Elements tool" },
    { key: "images", title: "Images", icon: <ImageIcon size={12} className="text-[#FFC53D]" />, addTitle: "Upload media", onAdd: () => uploadMediaInputRef.current?.click(), kind: "media", items: imageTracks, empty: "Drop images from Project Media here" },
    { key: "video", title: "Video", icon: <Video size={12} className="text-[#8B5CF6]" />, addTitle: "Upload media", onAdd: () => uploadMediaInputRef.current?.click(), kind: "media", items: videoTracks, empty: "No video clips" },
    { key: "audio", title: "Audio", icon: <AudioLines size={12} className="text-[#00F0FF]" />, addTitle: "Upload media", onAdd: () => uploadMediaInputRef.current?.click(), kind: "media", items: audioTracks, empty: "No audio clips" },
  ];
  const groupHeaderLabel = (g) => (
    <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-[#111214] px-2" style={{ height: GROUP_HEADER_H }} data-testid={`group-header-${g.key}`}>
      <button onClick={() => toggleGroup(g.key)} data-testid={`group-toggle-${g.key}`} className="flex min-w-0 items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] hover:text-white">
        {groupCollapsed[g.key] ? <ChevronRight size={12} /> : <ChevronDown size={12} />} {g.icon} <span className="truncate">{g.title}</span>
      </button>
      <button onClick={g.onAdd} title={g.addTitle} data-testid={`group-add-${g.key}`} className="rounded p-0.5 text-[#A1A1AA] hover:text-[#00F0FF]"><Plus size={12} /></button>
    </div>
  );
  const captionLayerLabel = (l) => (
    <div key={l.id} draggable
      onDragStart={(e) => { setDragLayerId(l.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", l.id); } catch { /* noop */ } }}
      onDragOver={(e) => { e.preventDefault(); if (l.id !== dragLayerId) setOverLayerId(l.id); }}
      onDragLeave={() => setOverLayerId((o) => (o === l.id ? null : o))}
      onDrop={(e) => { e.preventDefault(); reorderLayers(dragLayerId, l.id); setDragLayerId(null); setOverLayerId(null); }}
      onDragEnd={() => { setDragLayerId(null); setOverLayerId(null); }}
      data-testid={`layer-label-${l.id}`}
      className={`relative ${dropLayerId === l.id ? "ring-2 ring-inset ring-[#00F0FF]" : ""} ${dragLayerId === l.id ? "opacity-40" : ""}`}>
      {overLayerId === l.id && <div data-testid="layer-drop-indicator" className="pointer-events-none absolute -top-0.5 left-0 right-0 z-30 h-1 bg-[#00F0FF]" />}
      {labelCell(<Type size={12} />, l.name, l)}
    </div>
  );
  const captionLayerRow = (l) => (
    <div key={l.id} className={`relative border-b border-white/5 ${l.hidden ? "opacity-30" : ""} ${dropLayerId === l.id ? "bg-[#00F0FF]/10 ring-2 ring-inset ring-[#00F0FF]" : ""}`} style={{ height: ROW_H, width: innerW }} data-testid={`layer-track-${l.id}`}>
      {l.objects.filter((o) => inView(o) || (sel?.layerId === l.id && sel.ids.includes(o.id))).map((o) => <Block key={o.id} layer={l} obj={o} />)}
    </div>
  );
  const emptyLabelCell = (key) => <div key={`el-${key}`} className="flex items-center border-b border-white/5 bg-[#0d0d0d] px-3 text-[10px] text-[#555]" style={{ height: ROW_H, width: LABEL_W }} />;
  const emptyTrackRow = (key, text) => <div key={`et-${key}`} className="flex items-center border-b border-white/5 px-4 text-[11px] text-[#555]" style={{ height: ROW_H, width: innerW }}>{text}</div>;


  return (
    <div className="relative flex h-full flex-col border-t border-white/10 bg-[#0A0A0A]" data-testid="caption-timeline">
      {/* header */}
      <div className="flex flex-col gap-1.5 border-b border-white/10 px-2 py-1.5 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-2 sm:px-3 sm:py-2">
        <div className="flex min-w-0 items-center gap-2 whitespace-nowrap text-sm font-bold text-white">
          <button onClick={onToggleCollapse} data-testid="timeline-collapse-toggle" title={collapsed ? "Expand timeline" : "Collapse timeline"} className="rounded-lg p-1 text-[#A1A1AA] hover:bg-white/10 hover:text-white">
            {collapsed ? <ChevronsUpDown size={15} /> : <ChevronsDownUp size={15} />}
          </button>
          {onToggleExpanded && !collapsed && (
            <button onClick={onToggleExpanded} data-testid="timeline-expand-toggle" title={expanded ? "Restore layout" : "Maximize timeline"} className="rounded-lg p-1 text-[#A1A1AA] hover:bg-white/10 hover:text-white">
              {expanded ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
          <Sparkles size={15} className="hidden text-[#00F0FF] sm:block" /> <span className="hidden sm:inline">Caption Timeline</span>
          <span className="ml-2 rounded bg-white/5 px-2 py-0.5 font-plex text-[11px] tabular-nums text-[#A1A1AA]" data-testid="timeline-time" title="Time / Frame @ FPS">{fmtMs(currentTime)} <span className="text-[#666]">/</span> {fmtMs(dur)} <span className="ml-1 text-[#00F0FF]">f{frameOf(currentTime)}</span> <span className="text-[#555]">· {Math.round(FPS)}fps</span></span>
          {!autoFollow && (
            <button onClick={resumeAutoFollow} data-testid="resume-autofollow-btn" title="Auto-follow paused" className="ml-1 flex items-center gap-1 rounded-full border border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 py-0.5 text-[10px] font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/20">
              <LocateFixed size={11} /> Resume Auto-Follow
            </button>
          )}
        </div>
        <div className={`no-scrollbar flex flex-nowrap items-center gap-2 overflow-x-auto sm:flex-wrap sm:overflow-visible [&>*]:shrink-0 ${collapsed ? "hidden sm:flex" : ""}`} data-testid="timeline-tools">
          {/* Snap toggle + tolerance */}
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.02] px-1.5 py-0.5">
            <button onClick={() => setSnapOn((s) => !s)} data-testid="snap-toggle" title="Snap to edges & playhead"
              className={`flex items-center gap-1 rounded px-1.5 py-1 text-[11px] font-bold transition-colors ${snapOn ? "text-[#00F0FF]" : "text-[#666]"}`}>
              <Magnet size={13} /> SNAP {snapOn ? "ON" : "OFF"}
            </button>
            <select value={snapTolPx} onChange={(e) => setSnapTolPx(+e.target.value)} data-testid="snap-tolerance" disabled={!snapOn}
              className="rounded border border-white/10 bg-transparent px-1 py-0.5 text-[10px] text-[#A1A1AA] outline-none disabled:opacity-40">
              {TOL_PRESETS.map((p) => <option key={p} value={p} className="bg-[#141414]">{p}px</option>)}
            </select>
            <button onClick={() => setBeatsOn((b) => !b)} data-testid="beats-toggle" disabled={!snapOn || !(wave?.beats?.length)}
              title={wave?.beats?.length ? `Snap to ${wave.beats.length} detected beats` : (wave ? "No beats detected in this audio" : "Analysing audio…")}
              className={`flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-bold transition-colors disabled:opacity-40 ${beatsOn ? "text-[#FFC53D]" : "text-[#666]"}`}>
              <AudioLines size={12} /> BEATS
            </button>
          </div>
          {/* Zoom */}
          <div className="flex items-center gap-0.5 rounded-lg border border-white/10 bg-white/[0.02] px-1">
            <button onClick={() => applyZoom(zoom / 1.5)} data-testid="zoom-out" title="Zoom out" className="p-1 text-[#A1A1AA] hover:text-white"><ZoomOut size={14} /></button>
            <select value={ZOOM_PRESETS.includes(zoom) ? zoom : ""} onChange={(e) => e.target.value && applyZoom(+e.target.value)} data-testid="zoom-select"
              className="w-16 rounded bg-transparent px-1 py-0.5 text-center font-plex text-[11px] tabular-nums text-white outline-none">
              {!ZOOM_PRESETS.includes(zoom) && <option value="" className="bg-[#141414]">{Math.round(zoom * 100)}%</option>}
              {ZOOM_PRESETS.map((z) => <option key={z} value={z} className="bg-[#141414]">{Math.round(z * 100)}%</option>)}
            </select>
            <button onClick={() => applyZoom(zoom * 1.5)} data-testid="zoom-in" title="Zoom in" className="p-1 text-[#A1A1AA] hover:text-white"><ZoomIn size={14} /></button>
            <button onClick={fitTimeline} data-testid="zoom-fit" title="Fit timeline" className="ml-0.5 flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-semibold text-[#A1A1AA] hover:text-white"><Maximize2 size={12} /> Fit</button>
            {isFeatureEnabled("fit_edited_timeline") && <button onClick={fitEditedTimeline} data-testid="fit-edited-timeline" title="Fit the edited (trimmed) content to the viewport" className="ml-0.5 flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-semibold text-[#00F0FF] hover:text-white"><LocateFixed size={12} /> Fit Edited</button>}
          </div>
          <button onClick={undo} data-testid="caption-undo" title="Undo" className="rounded-lg p-1.5 text-[#A1A1AA] hover:bg-white/10 hover:text-white"><Undo2 size={14} /></button>
          <button onClick={redo} data-testid="caption-redo" title="Redo" className="rounded-lg p-1.5 text-[#A1A1AA] hover:bg-white/10 hover:text-white"><Redo2 size={14} /></button>
          {hasAny && <button onClick={addLayer} data-testid="add-layer-btn" className="flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white hover:border-[#00F0FF]/50"><Plus size={13} /> Layer</button>}
          <button onClick={() => setTwoLineOpen(true)} data-testid="add-two-line-btn" className="flex items-center gap-1 rounded-full border border-[#8B5CF6]/40 px-2.5 py-1.5 text-xs font-semibold text-[#c4b5fd] hover:border-[#8B5CF6]/70"><Type size={13} /> Two-Line</button>
          <input type="file" accept=".mp4,.mov,.webm,.m4v" ref={videoInputRef} className="hidden" data-testid="add-video-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addVideoClip(f); }} />
          <input type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" ref={audioInputRef} className="hidden" data-testid="add-music-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addMusic(f); }} />
          <input type="file" accept=".mp3,.wav,.m4a,.aac,.ogg,audio/*" ref={voiceInputRef} className="hidden" data-testid="add-voice-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addVoice(f); }} />
          <input type="file" accept=".png,.jpg,.jpeg,.webp,image/*" ref={imageInputRef} className="hidden" data-testid="add-image-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; addImageAsset(f); }} />
          {!nleLayout && (<>
          <button onClick={() => setShowMedia((v) => !v)} data-testid="media-panel-toggle" className={`flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-xs font-semibold ${showMedia ? "border-[#00F0FF]/60 text-[#00F0FF]" : "border-white/15 text-white hover:border-[#00F0FF]/50"}`}><LayersIcon size={13} /> Media</button>
          <button onClick={() => videoInputRef.current?.click()} disabled={uploading} data-testid="add-video-btn" className="flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white hover:border-[#8B5CF6]/60 disabled:opacity-50"><Video size={13} /> Video</button>
          <button onClick={() => setYtKind("video")} data-testid="add-yt-video-btn" title="Paste a YouTube link for video" className="flex items-center gap-1 rounded-full border border-[#FF3B3B]/40 px-2.5 py-1.5 text-xs font-semibold text-[#ff7a7a] hover:border-[#FF3B3B]/70"><Video size={13} /> YouTube</button>
          <button onClick={() => audioInputRef.current?.click()} disabled={uploading} data-testid="add-music-btn" className="flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white hover:border-[#00F0FF]/60 disabled:opacity-50">{uploading ? <Loader2 size={13} className="animate-spin" /> : <Music size={13} />} Music</button>
          <button onClick={() => setYtKind("audio")} data-testid="add-yt-audio-btn" title="Paste a YouTube link for audio" className="flex items-center gap-1 rounded-full border border-[#FF3B3B]/40 px-2.5 py-1.5 text-xs font-semibold text-[#ff7a7a] hover:border-[#FF3B3B]/70"><Music size={13} /> YouTube</button>
          {recording ? (
            <button onClick={stopRec} data-testid="voice-stop-btn" className="flex items-center gap-1 rounded-full border border-red-400/60 bg-red-500/15 px-2.5 py-1.5 text-xs font-semibold text-red-300"><span className="h-2 w-2 animate-pulse rounded-full bg-red-400" /> Stop</button>
          ) : (
            <div className="flex items-center gap-0.5 rounded-full border border-white/15 pl-2.5 pr-1">
              <button onClick={() => voiceInputRef.current?.click()} disabled={uploading} data-testid="add-voice-btn" className="flex items-center gap-1 py-1.5 text-xs font-semibold text-white disabled:opacity-50"><Mic size={13} /> Voice</button>
              <button onClick={startRec} disabled={uploading} data-testid="voice-record-btn" title="Record voiceover" className="rounded-full p-1 text-[#A1A1AA] hover:text-red-400"><Circle size={12} /></button>
            </div>
          )}
          </>)}
          <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="captions-mode" className="rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-xs text-white outline-none">
            {MODES.map((m) => <option key={m.v} value={m.v} className="bg-[#141414]">{m.l}</option>)}
          </select>
          <button onClick={createCaptions} disabled={busy} data-testid="create-captions-btn" className="flex items-center gap-1.5 rounded-full bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90 disabled:opacity-60">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create Captions
          </button>
          <button onClick={() => { setPickMode((v) => !v); if (pickMode) setPicked(new Set()); }} data-testid="tl-select-mode"
            title="Select: click each caption you want, then apply any preset, effect or animation to them"
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold transition-colors ${pickMode ? "border-[#FFC94D] bg-[#FFC94D] text-black" : "border-[#FFC94D]/50 text-[#FFC94D] hover:bg-[#FFC94D]/10"}`}>
            <MousePointerClick size={13} /> {pickMode ? `Selecting · ${picked.size}` : picked.size ? `Selected · ${picked.size}` : "Select"}
          </button>
          {(pickMode || picked.size > 0) && (<>
            <button onClick={() => setPicked(new Set())} data-testid="tl-select-clear" className="text-[11px] text-[#A1A1AA] hover:text-white">Clear</button>
            <button onClick={() => { setPickMode(false); }} data-testid="tl-select-done" className="text-[11px] font-semibold text-[#FFC94D] hover:underline">Done</button>
          </>)}
          <div className="relative">
            <button onClick={() => setSyncMenu((v) => !v)} data-testid="resync-edit-btn" title="Fit captions again after trimming / cutting the video"
              className="flex items-center gap-1.5 rounded-full border border-[#00F0FF]/40 px-3 py-1.5 text-xs font-bold text-[#00F0FF] hover:bg-[#00F0FF]/10"><RefreshCw size={13} /> Sync with edit</button>
            {syncMenu && (
              <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-xl border border-white/10 bg-[#141414] p-1.5 shadow-2xl" data-testid="resync-edit-menu">
                <button onClick={resyncToEdit} data-testid="resync-edit-captions" className="w-full rounded-lg px-3 py-2 text-left text-xs text-white hover:bg-white/5">
                  <div className="font-semibold">Synchronize with audio</div><div className="text-[10px] text-[#A1A1AA]">Instantly re-fit captions/lyrics to the trimmed video</div></button>
                {onTranscribeAgain && <button onClick={() => { setSyncMenu(false); onTranscribeAgain(); }} data-testid="resync-transcribe-again" className="w-full rounded-lg px-3 py-2 text-left text-xs text-white hover:bg-white/5">
                  <div className="font-semibold">Transcribe again</div><div className="text-[10px] text-[#A1A1AA]">Listen to the edited audio again (keeps your pasted lyrics) and rebuild captions</div></button>}
                {onGenerateInstead && <button onClick={() => { setSyncMenu(false); onGenerateInstead(); }} data-testid="resync-generate-instead" className="w-full rounded-lg px-3 py-2 text-left text-xs text-white hover:bg-white/5">
                  <div className="font-semibold">Generate transcript instead</div><div className="text-[10px] text-[#A1A1AA]">Not happy with your script's timing? Replace it with an automatic transcript</div></button>}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Batch: Project Media panel (non-NLE floating popover; NLE uses the left column) */}
      {!nleLayout && showMedia && !collapsed && (
        <div className="absolute right-3 top-12 z-50 w-72 rounded-xl border border-white/15 bg-[#0d0d0d]/98 p-3 shadow-2xl backdrop-blur" data-testid="project-media-panel-floating">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-bold tracking-wide text-white">PROJECT MEDIA</span>
            <button onClick={() => setShowMedia(false)} data-testid="media-panel-close" className="text-[#A1A1AA] hover:text-white"><X size={14} /></button>
          </div>
          <div className="max-h-56 space-y-1.5 overflow-y-auto pr-1">
            {mediaAssets.length === 0 && <p className="py-3 text-center text-[11px] text-[#666]">No uploads yet. Use + Video / + Music / + Voice to import.</p>}
            {mediaAssets.map((a) => (
              <div key={a.id} className="flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.02] p-1.5" data-testid={`media-asset-${a.id}`}>
                {a.kind === "audio" ? <AudioLines size={14} className="shrink-0 text-[#00F0FF]" /> : <Video size={14} className="shrink-0 text-[#8B5CF6]" />}
                <div className="min-w-0 flex-1">
                  <input value={a.name || ""} onChange={(e) => renameAsset(a.id, e.target.value)} data-testid={`media-asset-name-${a.id}`}
                    className="w-full truncate rounded bg-transparent text-[11px] text-white outline-none focus:bg-white/5" />
                  <span className="text-[9px] text-[#666]">{a.kind} · {Math.round(a.duration || 0)}s</span>
                </div>
                <button onClick={() => addAssetToTimeline(a)} data-testid={`media-asset-add-${a.id}`} title="Add to timeline" className="rounded p-1 text-[#A1A1AA] hover:text-[#00F0FF]"><Plus size={13} /></button>
                <button onClick={() => deleteAsset(a.id)} data-testid={`media-asset-delete-${a.id}`} title="Remove from Project Media" className="rounded p-1 text-[#A1A1AA] hover:text-red-400"><Trash2 size={13} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Batch 2 — live timing readout while dragging/resizing */}
      {dragInfo && (
        <div className="pointer-events-none absolute left-1/2 top-11 z-40 -translate-x-1/2 rounded-lg border border-[#00F0FF]/40 bg-[#0d0d0d]/95 px-3 py-1.5 font-plex text-[11px] tabular-nums text-white shadow-xl backdrop-blur" data-testid="drag-timing-readout">
          <span className={dragInfo.edge === "left" ? "text-[#00F0FF]" : ""}>START {fmtMs(dragInfo.start)} <span className="text-[#666]">f{frameOf(dragInfo.start)}</span></span>
          <span className="mx-2 text-[#333]">|</span>
          <span className={dragInfo.edge === "right" ? "text-[#00F0FF]" : ""}>END {fmtMs(dragInfo.end)} <span className="text-[#666]">f{frameOf(dragInfo.end)}</span></span>
          <span className="mx-2 text-[#333]">|</span>
          <span className="text-[#A1A1AA]">DUR {(dragInfo.end - dragInfo.start).toFixed(3)}s</span>
        </div>
      )}

      {/* body: fixed label column + scrollable tracks */}
      <div className={`flex min-h-0 flex-1 overflow-y-auto ${collapsed ? "hidden" : ""}`}>
        <div className="shrink-0 border-r border-white/10" style={{ width: LABEL_W }}>
          {GROUPS.map((g) => (
            <div key={`lg-${g.key}`}>
              {groupHeaderLabel(g)}
              {!groupCollapsed[g.key] && (
                g.items.length
                  ? (g.kind === "caption" ? g.items.map((l) => captionLayerLabel(l)) : g.items.map((t) => <div key={t.id}>{mediaLabelCell(t)}</div>))
                  : emptyLabelCell(g.key)
              )}
            </div>
          ))}
        </div>
        <div ref={scrollRef} style={{ touchAction: "pan-x pan-y" }} className="relative min-w-0 flex-1 select-none overflow-x-auto" onClick={seekFromX} onScroll={onTimelineScroll}
          onDragOver={(e) => { if (e.dataTransfer.types.includes("asset-id")) { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; } }} onDrop={onAssetDrop}>
          <div className="relative" style={{ width: innerW }}>
            {/* Playhead line (non-interactive so clips stay clickable) */}
            <div className="pointer-events-none absolute bottom-0 top-0 z-20 w-0.5 bg-[#00F0FF]" style={{ left: (currentTime || 0) * pxPerSec, boxShadow: "0 0 6px rgba(0,240,255,0.6)" }} data-testid="timeline-playhead" />
            {/* Draggable grip — sits in the top ruler/header band; larger hit area for easy grabbing */}
            <div data-testid="timeline-playhead-grip" onPointerDown={startPlayheadDrag} title="Drag to scrub"
              className="absolute top-0 z-40 flex cursor-ew-resize items-start justify-center"
              style={{ left: (currentTime || 0) * pxPerSec, transform: "translateX(-50%)", width: 22, height: GROUP_HEADER_H + 10 }}>
              <div className="mt-[2px] h-3.5 w-3.5 rotate-45 rounded-[3px] border border-white/80 bg-[#00F0FF] shadow-[0_0_10px_#00F0FF] transition-transform hover:scale-110" />
            </div>
            {guide && (
              <div className="pointer-events-none absolute bottom-0 top-0 z-30" style={{ left: guide.t * pxPerSec }} data-testid="snap-guide">
                <div className="h-full w-px bg-[#FFC53D]" style={{ boxShadow: "0 0 6px #FFC53D" }} />
                <div className="absolute top-1 left-1 rounded bg-[#FFC53D] px-1 py-0.5 font-plex text-[9px] font-bold tabular-nums text-black">{fmtMs(guide.t)}</div>
              </div>
            )}
            {GROUPS.map((g) => (
              <div key={`tg-${g.key}`}>
                <div className="border-b border-white/10 bg-[#111214]" style={{ height: GROUP_HEADER_H, width: innerW }} />
                {!groupCollapsed[g.key] && (
                  g.items.length
                    ? (g.kind === "caption" ? g.items.map((l) => captionLayerRow(l)) : g.items.map((t) => mediaTrackRow(t)))
                    : emptyTrackRow(g.key, g.empty)
                )}
              </div>
            ))}
            <RefTrack><div className="my-2 h-9" /></RefTrack>
          </div>
        </div>
      </div>

      {/* Batch 3 — media-clip inspector */}
      {(() => {
      const _inspectors = (<>
      {!collapsed && mediaClipSel && mediaClipSel.kind === "element" && (
        <div className="flex flex-wrap items-center gap-2 border-t border-white/10 bg-[#0d0d0d] px-3 py-2" data-testid="element-inspector">
          <span className="rounded-full bg-[#EC4899]/15 px-2 py-1 text-[11px] font-bold capitalize text-[#EC4899]">{mediaClipSel.element || "shape"}</span>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Type
            <select value={mediaClipSel.element || "rect"} data-testid="element-type-select"
              onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { element: e.target.value })}
              className="rounded border border-white/10 bg-[#1a1a1a] px-1.5 py-1 text-white outline-none">
              {ELEMENT_TYPES.map((x) => <option key={x.v} value={x.v} className="bg-[#141414]">{x.l}</option>)}
            </select></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Start<input type="number" step="0.1" value={mediaClipSel.start} data-testid="element-start-input"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { start: Math.max(0, Math.min(mediaClipSel.end - MIN_LEN, +e.target.value)) })} className="w-16 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">End<input type="number" step="0.1" value={mediaClipSel.end} data-testid="element-end-input"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { end: Math.min(dur, Math.max(mediaClipSel.start + MIN_LEN, +e.target.value)) })} className="w-16 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
          {mediaClipSel.element !== "line" && mediaClipSel.element !== "arrow" && (
            <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Fill<input type="color" value={mediaClipSel.fill || "#00F0FF"} data-testid="element-fill"
              onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { fill: e.target.value })} className="h-6 w-8 rounded border border-white/10 bg-transparent" /></label>
          )}
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Stroke<input type="color" value={mediaClipSel.stroke || "#000000"} data-testid="element-stroke"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { stroke: e.target.value })} className="h-6 w-8 rounded border border-white/10 bg-transparent" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Weight<input type="range" min="0" max="0.03" step="0.001" value={mediaClipSel.strokeWidth ?? 0} data-testid="element-stroke-width"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { strokeWidth: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">W<input type="range" min="0.02" max="1" step="0.01" value={mediaClipSel.w ?? 0.3} data-testid="element-w"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { w: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          {mediaClipSel.element !== "line" && mediaClipSel.element !== "arrow" && (
            <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">H<input type="range" min="0.02" max="1" step="0.01" value={mediaClipSel.h ?? 0.15} data-testid="element-h"
              onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { h: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          )}
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">X<input type="range" min="0" max="1" step="0.01" value={mediaClipSel.x ?? 0.5} data-testid="element-x"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { x: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Y<input type="range" min="0" max="1" step="0.01" value={mediaClipSel.y ?? 0.5} data-testid="element-y"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { y: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Opacity<input type="range" min="0" max="1" step="0.05" value={mediaClipSel.opacity ?? 1} data-testid="element-opacity"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { opacity: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Rotate<input type="range" min="-180" max="180" step="1" value={mediaClipSel.rotation ?? 0} data-testid="element-rotation"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { rotation: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          {mediaClipSel.element === "rounded" && (
            <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Radius<input type="range" min="0" max="0.5" step="0.02" value={mediaClipSel.cornerRadius ?? 0.22} data-testid="element-corner-radius"
              onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { cornerRadius: +e.target.value })} className="w-16 accent-[#EC4899]" /></label>
          )}
          <button onClick={() => { commitMedia(mediaTracks.map((tr) => tr.id !== mediaSel.trackId ? tr : { ...tr, objects: tr.objects.filter((o) => o.id !== mediaSel.objId) }).filter((tr) => tr.type !== "element" || (tr.objects || []).length)); setMediaSel(null); }}
            data-testid="element-delete-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:border-red-400/50 hover:text-red-400"><Trash2 size={13} /> Delete</button>
        </div>
      )}
      {!collapsed && mediaClipSel && mediaClipSel.kind !== "element" && (
        <div className="flex flex-wrap items-center gap-2 border-t border-white/10 bg-[#0d0d0d] px-3 py-2" data-testid="media-inspector">
          <span className="rounded-full bg-white/5 px-2 py-1 text-[11px] font-bold text-white">{mediaClipSel.kind === "audio" ? "Audio clip" : mediaClipSel.kind === "image" ? "Image" : "Video clip"}</span>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Start<input type="number" step="0.1" value={mediaClipSel.start} data-testid="media-start-input"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { start: Math.max(0, Math.min(mediaClipSel.end - MIN_LEN, +e.target.value)) })} className="w-20 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
          <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">End<input type="number" step="0.1" value={mediaClipSel.end} data-testid="media-end-input"
            onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { end: Math.min(dur, Math.max(mediaClipSel.start + MIN_LEN, +e.target.value)) })} className="w-20 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
          {mediaClipSel.kind === "audio" && (
            <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Vol<input type="range" min="0" max="1.5" step="0.05" value={mediaClipSel.volume ?? 1} data-testid="media-volume"
              onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { volume: +e.target.value })} className="w-20 accent-[#00F0FF]" /><span className="w-8 tabular-nums">{Math.round((mediaClipSel.volume ?? 1) * 100)}%</span></label>
          )}
          {mediaClipSel.kind === "audio" && (
            <>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Fade in<input type="number" min="0" step="0.1" value={mediaClipSel.fade_in ?? 0} data-testid="media-fade-in"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { fade_in: Math.max(0, +e.target.value) })} className="w-16 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Fade out<input type="number" min="0" step="0.1" value={mediaClipSel.fade_out ?? 0} data-testid="media-fade-out"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { fade_out: Math.max(0, +e.target.value) })} className="w-16 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
              {[["denoise", "Denoise"], ["enhance", "Enhance"], ["normalize", "Normalize"]].map(([k, l]) => (
                <button key={k} onClick={() => patchMediaObj(mediaSel.trackId, mediaSel.objId, { [k]: !mediaClipSel[k] })} data-testid={`media-audio-${k}`}
                  className={`rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors ${mediaClipSel[k] ? "border-[#00F0FF]/60 bg-[#00F0FF]/10 text-[#00F0FF]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>{l}</button>
              ))}
            </>
          )}
          {mediaClipSel.kind === "image" && (
            <>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Size<input type="range" min="0.05" max="1" step="0.01" value={mediaClipSel.scale ?? 0.5} data-testid="image-scale"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { scale: +e.target.value })} className="w-20 accent-[#FFC53D]" /><span className="w-8 tabular-nums">{Math.round((mediaClipSel.scale ?? 0.5) * 100)}%</span></label>
              {isFeatureEnabled("image_shape_fit") && (
                <>
                  <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Shape
                    <select value={mediaClipSel.shape || "rect"} data-testid="image-shape"
                      onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { shape: e.target.value })}
                      className="rounded border border-white/10 bg-[#1a1a1a] px-1.5 py-1 text-white outline-none">
                      <option value="rect">Rectangle</option><option value="rounded">Rounded</option>
                      <option value="circle">Circle</option><option value="oval">Oval</option><option value="square">Square</option>
                    </select></label>
                  <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Fit
                    <select value={mediaClipSel.fit || "contain"} data-testid="image-fit"
                      onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { fit: e.target.value })}
                      className="rounded border border-white/10 bg-[#1a1a1a] px-1.5 py-1 text-white outline-none">
                      <option value="contain">Contain</option><option value="cover">Cover / Crop</option>
                      <option value="fill">Fill</option><option value="stretch">Stretch</option>
                    </select></label>
                  {(mediaClipSel.shape === "rounded" || (mediaClipSel.cornerRadius || 0) > 0) && (
                    <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Radius<input type="range" min="0" max="0.5" step="0.02" value={mediaClipSel.cornerRadius ?? 0.12} data-testid="image-corner-radius"
                      onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { cornerRadius: +e.target.value })} className="w-16 accent-[#FFC53D]" /></label>
                  )}
                </>
              )}
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Effect<select value={mediaClipSel.effect?.type || "none"} data-testid="image-effect-select"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { effect: e.target.value === "none" ? null : { type: e.target.value, duration: mediaClipSel.effect?.duration || 0.7, intensity: mediaClipSel.effect?.intensity ?? 1 } })}
                className="rounded border border-white/10 bg-[#141414] px-1.5 py-1 text-white">
                {IMAGE_EFFECTS.map((x) => <option key={x.v} value={x.v} className="bg-[#141414]">{x.l}</option>)}
              </select></label>
              {mediaClipSel.effect?.type && mediaClipSel.effect.type !== "none" && (
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]" title="Effect duration (seconds)">Dur<input type="range" min="0.2" max="3" step="0.1" value={mediaClipSel.effect?.duration || 0.7} data-testid="image-effect-dur"
                  onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { effect: { ...(mediaClipSel.effect || { type: "fadein" }), duration: +e.target.value } })} className="w-16 accent-[#FFC53D]" /><span className="w-6 tabular-nums">{(mediaClipSel.effect?.duration || 0.7).toFixed(1)}s</span></label>
              )}
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Opacity<input type="range" min="0" max="1" step="0.05" value={mediaClipSel.opacity ?? 1} data-testid="image-opacity"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { opacity: +e.target.value })} className="w-20 accent-[#FFC53D]" /></label>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Rotate<input type="range" min="-180" max="180" step="1" value={mediaClipSel.rotation ?? 0} data-testid="image-rotation"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { rotation: +e.target.value })} className="w-20 accent-[#FFC53D]" /><span className="w-8 tabular-nums">{Math.round(mediaClipSel.rotation ?? 0)}°</span></label>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">X<input type="range" min="0" max="1" step="0.01" value={mediaClipSel.x ?? 0.5} data-testid="image-x"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { x: +e.target.value })} className="w-16 accent-[#FFC53D]" /></label>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Y<input type="range" min="0" max="1" step="0.01" value={mediaClipSel.y ?? 0.5} data-testid="image-y"
                onChange={(e) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { y: +e.target.value })} className="w-16 accent-[#FFC53D]" /></label>
              {isFeatureEnabled("background_removal") && (
                mediaClipSel.cutout_asset_id ? (
                  <div className="flex items-center gap-1 rounded-lg border border-[#FFC53D]/40 p-0.5" data-testid="image-bg-toggle">
                    <button onClick={() => toggleBgRemoved(false)} data-testid="image-bg-original-btn"
                      className={`rounded-md px-2 py-1 text-xs font-semibold ${!mediaClipSel.bgRemoved ? "bg-[#FFC53D] text-black" : "text-[#FFC53D] hover:bg-[#FFC53D]/10"}`}>Original</button>
                    <button onClick={() => toggleBgRemoved(true)} data-testid="image-bg-removed-btn"
                      className={`rounded-md px-2 py-1 text-xs font-semibold ${mediaClipSel.bgRemoved ? "bg-[#FFC53D] text-black" : "text-[#FFC53D] hover:bg-[#FFC53D]/10"}`}>BG Removed</button>
                  </div>
                ) : (
                  <button onClick={() => { const a = mediaAssets.find((x) => x.id === mediaClipSel.asset_id); if (a) removeAssetBg(a); }} disabled={rembgBusy} data-testid="image-remove-bg-btn"
                    className="flex items-center gap-1 rounded-lg border border-[#FFC53D]/40 px-2.5 py-1.5 text-xs font-semibold text-[#FFC53D] hover:bg-[#FFC53D]/10 disabled:opacity-50">{rembgBusy ? <Loader2 size={13} className="animate-spin" /> : <Eraser size={13} />} Remove BG</button>
                )
              )}
            </>
          )}
          {(mediaClipSel.kind === "video" || mediaClipSel.kind === "image") && (() => {
            const f = mediaClipSel.filters || {};
            const setF = (k, v) => patchMediaObj(mediaSel.trackId, mediaSel.objId, { filters: { ...f, [k]: v } });
            return (
              <div className="flex flex-wrap items-center gap-2 border-l border-white/10 pl-2" data-testid="media-fx">
                <Palette size={13} className="text-[#8B5CF6]" />
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Bright<input type="range" min="0.4" max="1.6" step="0.05" value={f.brightness ?? 1} data-testid="fx-brightness"
                  onChange={(e) => setF("brightness", +e.target.value)} className="w-16 accent-[#8B5CF6]" /></label>
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Contrast<input type="range" min="0.4" max="1.8" step="0.05" value={f.contrast ?? 1} data-testid="fx-contrast"
                  onChange={(e) => setF("contrast", +e.target.value)} className="w-16 accent-[#8B5CF6]" /></label>
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Blur<input type="range" min="0" max="12" step="0.5" value={f.blur ?? 0} data-testid="fx-blur"
                  onChange={(e) => setF("blur", +e.target.value)} className="w-16 accent-[#8B5CF6]" /></label>
                <button onClick={() => setF("grayscale", !f.grayscale)} data-testid="fx-grayscale"
                  className={`rounded-lg border px-2 py-1 text-xs font-semibold ${f.grayscale ? "border-[#8B5CF6]/60 bg-[#8B5CF6]/10 text-[#8B5CF6]" : "border-white/15 text-[#A1A1AA] hover:text-white"}`}>B&amp;W</button>
                {(f.brightness != null && f.brightness !== 1) || (f.contrast != null && f.contrast !== 1) || f.blur || f.grayscale ? (
                  <button onClick={() => patchMediaObj(mediaSel.trackId, mediaSel.objId, { filters: {} })} data-testid="fx-reset" title="Reset effects" className="rounded-lg border border-white/15 px-2 py-1 text-xs text-[#A1A1AA] hover:text-white">Reset</button>
                ) : null}
              </div>
            );
          })()}
          {mediaClipSel.kind === "video" && (
            <VideoCropOpacity clip={mediaClipSel} meta={mediaClipSel.asset_id ? mediaAssets.find((a) => a.id === mediaClipSel.asset_id) : projectMeta}
              onPatch={(patch) => patchMediaObj(mediaSel.trackId, mediaSel.objId, patch)}
              onCropOnPreview={onCropEdit ? () => onCropEdit({ trackId: mediaSel.trackId, objId: mediaSel.objId }) : null} cropEditing={cropEditing} />
          )}
          {mediaClipSel.kind === "video" && (() => {
            const tr = mediaClipSel.transition || { type: "none", duration: 0.5 };
            const setTr = (patch) => {
              const next = { type: tr.type || "none", duration: tr.duration ?? 0.5, direction: tr.direction || "right", ...patch };
              patchMediaObj(mediaSel.trackId, mediaSel.objId, { transition: next });
              // Instantly show the effect: jump the preview to a representative mid-transition
              // frame (debounced so typing a duration doesn't thrash the playhead).
              if (onSeek) {
                const d = next.type && next.type !== "none" ? Math.min(next.duration || 0.5, 1.5) : 0;
                const at = (mediaClipSel.start || 0) + (d > 0 ? d * 0.5 : 0.1);
                clearTimeout(trPreviewRef.current);
                trPreviewRef.current = setTimeout(() => onSeek(at), 100);
              }
            };
            return (
              <div className="flex flex-wrap items-center gap-2 border-l border-white/10 pl-2" data-testid="media-transition">
                <Sparkles size={13} className="text-[#00F0FF]" />
                <span className="text-xs text-[#A1A1AA]">Transition</span>
                <select value={tr.type || "none"} onChange={(e) => setTr({ type: e.target.value })} data-testid="transition-type"
                  className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-xs text-white outline-none">
                  {[["none", "None"], ["fade", "Fade"], ["crossfade", "Crossfade"], ["slide", "Slide"], ["zoom", "Zoom"]].map(([v, l]) => <option key={v} value={v} className="bg-[#141414]">{l}</option>)}
                </select>
                {tr.type && tr.type !== "none" && (
                  <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Dur<input type="number" min="0.1" max="3" step="0.1" value={tr.duration ?? 0.5} data-testid="transition-duration"
                    onChange={(e) => setTr({ duration: Math.max(0.1, Math.min(3, +e.target.value)) })} className="w-14 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" />s</label>
                )}
                {tr.type === "slide" && (
                  <select value={tr.direction || "right"} onChange={(e) => setTr({ direction: e.target.value })} data-testid="transition-direction"
                    className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-xs text-white outline-none">
                    <option value="right" className="bg-[#141414]">From right</option>
                    <option value="left" className="bg-[#141414]">From left</option>
                  </select>
                )}
              </div>
            );
          })()}
          {mediaClipSel.kind === "video" && (() => {
            const eff = mediaClipSel.effect || "none";
            const eInt = typeof mediaClipSel.effectIntensity === "number" ? mediaClipSel.effectIntensity : 60;
            const setEff = (patch) => patchMediaObj(mediaSel.trackId, mediaSel.objId, patch);
            return (
              <div className="flex flex-wrap items-center gap-2 border-l border-white/10 pl-2" data-testid="video-style-inspector">
                <Sparkles size={13} className="text-[#30D158]" />
                <span className="text-xs text-[#A1A1AA]">Style</span>
                <select value={eff} onChange={(e) => setEff({ effect: e.target.value })} data-testid="video-style-effect"
                  className="rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-xs text-white outline-none">
                  {[["none", "None"], ["anime", "Anime"], ["cartoon", "Cartoon"]].map(([v, l]) => <option key={v} value={v} className="bg-[#141414]">{l}</option>)}
                </select>
                {eff !== "none" && (
                  <label className="flex items-center gap-1 text-xs text-[#A1A1AA]" title="Effect intensity">
                    Intensity
                    <input type="range" min="0" max="100" step="5" value={eInt} data-testid="video-style-intensity-range"
                      onChange={(e) => setEff({ effectIntensity: +e.target.value })} className="w-20 accent-[#30D158]" />
                    <span className="w-7 tabular-nums text-white">{eInt}</span>
                  </label>
                )}
              </div>
            );
          })()}
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex items-center gap-0.5 rounded-lg border border-white/15 bg-white/[0.02] px-1" data-testid="media-layer-move">
              <button onClick={() => moveMediaTrack(-1)} disabled={mediaTracks.findIndex((t) => t.id === mediaSel.trackId) <= 0} data-testid="media-move-up-layer" title="Move layer up" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF] disabled:opacity-30"><ArrowUp size={13} /></button>
              <button onClick={() => moveMediaTrack(1)} disabled={mediaTracks.findIndex((t) => t.id === mediaSel.trackId) >= mediaTracks.length - 1} data-testid="media-move-down-layer" title="Move layer down" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF] disabled:opacity-30"><ArrowDown size={13} /></button>
            </div>
            <div className="flex items-center gap-0.5 rounded-lg border border-white/15 bg-white/[0.02] px-1" data-testid="media-time-move">
              <button onClick={() => nudgeMediaTime(-1)} disabled={(mediaClipSel.start || 0) < 1} data-testid="media-move-back-1s" title="Move backward 1 second" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF] disabled:opacity-30"><ArrowLeft size={13} /></button>
              <button onClick={() => nudgeMediaTime(1)} data-testid="media-move-fwd-1s" title="Move forward 1 second" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF]"><ArrowRight size={13} /></button>
            </div>
            <button onClick={splitMediaAtPlayhead} data-testid="media-split-btn" title="Split at playhead" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><Scissors size={13} /> Split</button>
            <button onClick={duplicateMediaClip} data-testid="media-duplicate-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><CopyPlus size={13} /> Duplicate</button>
            {mediaClipSel.kind !== "image" && (
              <button onClick={() => patchMediaObj(mediaSel.trackId, mediaSel.objId, { muted: !mediaClipSel.muted })} data-testid="media-clip-mute-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white">{mediaClipSel.muted ? <VolumeX size={13} /> : <Volume2 size={13} />} {mediaClipSel.muted ? "Unmute" : "Mute"}</button>
            )}
            {mediaClipSel.kind !== "image" && (
              <button onClick={toggleLink} data-testid="media-unlink-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-[#00F0FF]">{linkedPair().linked ? <><Unlink size={13} /> Unlink</> : <><Link2 size={13} /> Relink</>}</button>
            )}
            <button onClick={deleteMediaClip} data-testid="media-delete-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:border-red-400/50 hover:text-red-400"><Trash2 size={13} /> Delete</button>
          </div>
        </div>
      )}

      {/* inspector */}
      {!collapsed && selectedObjs.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 border-t border-white/10 bg-[#0d0d0d] px-3 py-2" data-testid="caption-inspector">
          {single ? (
            <>
              <input value={single.text} onChange={(e) => setCaptionText(selLayer.id, single.id, e.target.value)}
                onBlur={() => offerTranscriptSync(single)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} data-testid="caption-text-input"
                className="min-w-[150px] flex-1 rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm outline-none focus:border-[#00F0FF]/50" />
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Start<input type="number" step="0.1" value={single.start} onChange={(e) => updateObj(selLayer.id, single.id, { start: Math.max(0, +e.target.value), timeLocked: true })} data-testid="caption-start-input" className="w-20 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
              <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">End<input type="number" step="0.1" value={single.end} onChange={(e) => updateObj(selLayer.id, single.id, { end: +e.target.value, timeLocked: true })} data-testid="caption-end-input" className="w-20 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
              <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.02] px-2 py-1" data-testid="caption-position-controls">
                <Move size={12} className="text-[#00F0FF]" title="Position (drag on the preview too)" />
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">X%<input type="number" min="0" max="100" step="1" data-testid="caption-posx-input"
                  value={single.posX != null ? Math.round(single.posX * 100) : ""} placeholder="50"
                  onChange={(e) => { const v = e.target.value; updateObj(selLayer.id, single.id, { posX: v === "" ? null : Math.max(0, Math.min(1, (+v) / 100)) }); }}
                  className="w-14 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Y%<input type="number" min="0" max="100" step="1" data-testid="caption-posy-input"
                  value={single.posY != null ? Math.round(single.posY * 100) : ""} placeholder="72"
                  onChange={(e) => { const v = e.target.value; updateObj(selLayer.id, single.id, { posY: v === "" ? null : Math.max(0, Math.min(1, (+v) / 100)) }); }}
                  className="w-14 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
                <label className="flex items-center gap-1 text-xs text-[#A1A1AA]">Size%<input type="number" min="20" max="500" step="5" data-testid="caption-size-input"
                  value={Math.round((single.scale ?? 1) * 100)} placeholder="100"
                  onChange={(e) => { const v = e.target.value; updateObj(selLayer.id, single.id, { scale: v === "" ? 1 : Math.max(0.2, Math.min(5, (+v) / 100)) }); }}
                  className="w-16 rounded border border-white/10 bg-white/[0.03] px-2 py-1 text-white" /></label>
                <button onClick={() => updateObj(selLayer.id, single.id, { posX: 0.5 })} data-testid="caption-center-h" title="Center horizontally" className="rounded p-1 text-[#A1A1AA] hover:text-[#00F0FF]"><AlignHorizontalJustifyCenter size={14} /></button>
                <button onClick={() => updateObj(selLayer.id, single.id, { posY: 0.5 })} data-testid="caption-center-v" title="Center vertically" className="rounded p-1 text-[#A1A1AA] hover:text-[#00F0FF]"><AlignVerticalJustifyCenter size={14} /></button>
                <button onClick={() => updateObj(selLayer.id, single.id, { posX: null, posY: null })} data-testid="caption-reset-pos" title="Reset position" className="rounded p-1 text-[#A1A1AA] hover:text-[#00F0FF]"><Crosshair size={14} /></button>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2 text-sm text-white" data-testid="phrase-selection-label">
              <span className="rounded-full bg-[#00F0FF]/15 px-2 py-0.5 text-[11px] font-bold text-[#00F0FF]">{selectedObjs.length} selected</span>
              <span className="max-w-[240px] truncate font-semibold">"{phraseText}"</span>
            </div>
          )}
          <CaptionMotionControl obj={selectedObjs[0]} onPatch={(p) => (p.motion !== undefined && (p.motion?.type || "none") !== (selectedObjs[0].motion?.type || "none") ? askAndPatch(p, `${p.motion?.type || "no"} motion`) : patchSelected(p))}
            onTrack={onTrackStart && selectedObjs.length === 1 ? () => onTrackStart({ layerId: sel.layerId, objId: selectedObjs[0].id }) : null} />
          <CaptionFxControl fx={selectedObjs[0].fx} onChange={(fx) => ((fx?.type || "none") !== (selectedObjs[0].fx?.type || "none") ? askAndPatch({ fx }, "look effect") : patchSelected({ fx }))} testid="insp-caption-fx" />
          <CaptionPlacement obj={selectedObjs[0]} count={selectedObjs.length} onPatch={patchSelected} onPatchEnd={() => { liveDragRef.current = false; }}
            defaultY={captionConfig?.posY ?? ({ top: 0.14, "upper-middle": 0.30, center: 0.5, "lower-middle": 0.72, "lower-third": 0.72, bottom: 0.86 }[captionConfig?.position] ?? 0.72)} />
          {onStyleWord && (
            <div className="flex flex-wrap items-center gap-1" data-testid="insp-word-styles">
              <span className="mr-1 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#A1A1AA]"><Palette size={11} /> Style words</span>
              {selectedObjs.flatMap((o) => (o.words && o.words.length ? o.words : [{ word: o.text, word_id: o.word_id }])).filter((w) => w.word_id).slice(0, 40).map((w) => (
                <button key={w.word_id} onClick={() => onStyleWord(w.word_id, w.word)} data-testid={`insp-word-chip-${w.word_id}`} title="Redesign this word"
                  className={`rounded-md border px-1.5 py-0.5 text-[11px] font-semibold transition-colors ${(captionConfig?.wordStyles || {})[w.word_id] ? "border-[#8B5CF6]/60 bg-[#8B5CF6]/15 text-[#c4b5fd]" : "border-white/15 text-white/80 hover:border-[#00F0FF]/60 hover:text-[#00F0FF]"}`}>{w.word}</button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-1.5">
            <div className="flex items-center gap-0.5 rounded-lg border border-white/15 bg-white/[0.02] px-1" data-testid="caption-layer-move">
              <button onClick={() => moveSelToLayer(-1)} disabled={!canMoveUp} data-testid="caption-move-up-layer" title="Move layer up" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF] disabled:opacity-30"><ArrowUp size={13} /></button>
              <button onClick={() => moveSelToLayer(1)} data-testid="caption-move-down-layer" title="Move layer down" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF]"><ArrowDown size={13} /></button>
            </div>
            <div className="flex items-center gap-0.5 rounded-lg border border-white/15 bg-white/[0.02] px-1" data-testid="caption-time-move">
              <button onClick={() => nudgeCaptionTime(-1)} disabled={!captionCanBack} data-testid="caption-move-back-1s" title="Move backward 1 second" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF] disabled:opacity-30"><ArrowLeft size={13} /></button>
              <button onClick={() => nudgeCaptionTime(1)} data-testid="caption-move-fwd-1s" title="Move forward 1 second" className="rounded p-1.5 text-[#A1A1AA] hover:text-[#00F0FF]"><ArrowRight size={13} /></button>
            </div>
            <button onClick={splitCaption} disabled={!canSplit} data-testid="split-caption-btn" title="Split at playhead" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-40"><Scissors size={13} /> Split</button>
            <button onClick={mergeCaptions} disabled={selectedObjs.length < 2} data-testid="merge-captions-btn" title="Merge selected" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-40"><GitMerge size={13} /> Merge</button>
            <button onClick={duplicatePhrase} data-testid="duplicate-phrase-btn" className="flex items-center gap-1 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/90"><CopyPlus size={13} /> Duplicate</button>
            <button onClick={copyStyle} data-testid="copy-style-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><Palette size={13} /> Copy</button>
            <button onClick={pasteStyle} disabled={!copiedStyle} data-testid="paste-style-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white disabled:opacity-40"><ClipboardPaste size={13} /> Paste</button>
            <button onClick={saveAsPreset} data-testid="save-as-preset-btn" title="Save this caption's look as a preset" className="flex items-center gap-1 rounded-lg border border-[#8B5CF6]/40 bg-[#8B5CF6]/5 px-2.5 py-1.5 text-xs font-semibold text-[#a78bfa] hover:bg-[#8B5CF6]/10"><LayersIcon size={13} /> Save Preset</button>
            {single?.preset && <button onClick={resetPreset} data-testid="reset-preset-btn" title={`Reset to "${single.preset.name}"`} className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><Undo2 size={13} /> Reset</button>}
            {single?.preset && <button onClick={detachPreset} data-testid="detach-preset-btn" title="Detach from preset" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><Unlink size={13} /> Detach</button>}
            {isFeatureVisible("text_to_speech") && (
              <button onClick={() => openTts({ text: phraseText, atTime: selectedObjs[0]?.start ?? 0 })} data-testid="caption-generate-voice-btn" title="Generate AI voice from this caption" className="flex items-center gap-1 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/5 px-2.5 py-1.5 text-xs font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/10"><Sparkles size={13} /> Generate Voice</button>
            )}
            <button onClick={() => deleteObjs(sel.layerId, sel.ids)} data-testid="caption-delete-btn" className="flex items-center gap-1 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:border-red-400/50 hover:text-red-400"><Trash2 size={13} /> Delete</button>
          </div>
        </div>
      )}
      {nleLayout && !collapsed && !mediaClipSel && selectedObjs.length === 0 && inspectorVisible && projectSettingsPanel}
      </>);
      const _presetLib = null; void _presetLib;
      // Caption Presets now live in the FIXED right rail (presetPortalEl) — see the
      // dedicated portal below. The Inspector portal/inline only carries per-caption
      // style + clip-effect controls (never the preset library), so switching layouts
      // or focusing the preview can never move/hide the presets or the timeline.
      if (nleLayout && inspectorPortalEl && inspectorVisible)
        return createPortal(<div className="flex h-full flex-col overflow-y-auto" data-testid="nle-inspector">{_inspectors}</div>, inspectorPortalEl);
      // Content-first: in NLE mode the inspector lives ONLY in the floating Properties popup.
      // The inline strip is used only in the legacy (non-NLE) inline layout.
      if (!nleLayout && (mediaClipSel || selectedObjs.length > 0))
        return <div className="border-t border-white/10" data-testid="inline-inspector">{_inspectors}</div>;
      return null;
      })()}
      {/* Caption Presets — FIXED right-side rail (own scroll; independent of layout/preview-focus) */}
      {presetPortalEl && isFeatureEnabled("caption_presets") &&
        createPortal(
          <CaptionPresetsLibrary selectedCount={selectedObjs.length} totalCaptions={captionObjCount}
            onApply={requestPreset} appliedPresetId={single?.preset?.id} projectId={isDemo ? null : projectId}
            onSave={single ? saveAsPreset : null} refreshKey={presetsVersion} />,
          presetPortalEl)}
      {nleLayout && mediaPortalEl && createPortal(mediaPanelContent, mediaPortalEl)}
      {ytKind && <YoutubeMediaDialog projectId={projectId} kind={ytKind} onClose={() => setYtKind(null)} onAdded={(a) => onAssetAdded && onAssetAdded(a)}
        onUploadInstead={() => (ytKind === "audio" ? audioInputRef : videoInputRef).current?.click()} />}


      {/* delete-layer confirm */}
      {confirmDel && (
        <div className="absolute inset-0 z-40 flex items-center justify-center bg-black/60 p-4" data-testid="delete-layer-confirm">
          <div className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#141414] p-5">
            <div className="mb-1 flex items-center gap-2 font-display text-lg font-bold text-white"><LayersIcon size={18} className="text-red-400" /> Delete “{confirmDel.name}”?</div>
            <p className="mb-4 text-sm text-[#A1A1AA]">This removes {confirmDel.count} caption object{confirmDel.count === 1 ? "" : "s"} from the timeline. Your transcript and video are not affected.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} data-testid="delete-layer-cancel" className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10">Cancel</button>
              <button onClick={confirmDeleteLayer} data-testid="delete-layer-confirm-btn" className="rounded-full bg-red-500 px-4 py-2 text-sm font-bold text-white hover:bg-red-600">Delete Layer</button>
            </div>
          </div>
        </div>
      )}
      <TtsPanel open={ttsOpen} onClose={() => setTtsOpen(false)} initialText={ttsInit.text} atTime={ttsInit.atTime} projectId={isDemo ? null : projectId} onGenerated={onTtsGenerated} />
      <PresetsPanel open={presetsOpen} onClose={() => setPresetsOpen(false)} onApply={applyPresetTo} selectedCount={selectedObjs.length} totalCaptions={captionObjCount} />
      <TwoLineModal open={twoLineOpen} onOpenChange={setTwoLineOpen} config={{}} duration={duration} onAdd={addTwoLine} />
    </div>
  );
}
